/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SWModules.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 24-07-2024
 * @description : Component to add, update and replace software modules.
 */
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";


import appTheme from "../../assets/AppTheme/appTheme";
import customConsole from "../../config/customConsole";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  adminSettingsAPI,
  HandleApiActions,
  labCar,
} from "../../redux/actions";
import { useNavigate } from "react-router-dom";
import editRequestAPI from "../../redux/actions/editRequestApi";
import LockIcon from "@mui/icons-material/Lock";
import NoEncryptionGmailerrorredIcon from "@mui/icons-material/NoEncryptionGmailerrorred";
import moment from "moment";
import actionType from "../../redux/actionTypes";
import GithubSWMLogs from "./GithubSWMLogs";
import githubIntegrationAction from "../../redux/actions/githubIntegrationAction";
import typeApprovalAPI from "../../redux/actions/typeApprovalAPI";
import ruleEngineActions from "../../redux/actions/ruleEngineAction";
import {
  vlcCardStyles,
  vlcComponentStyle,
} from "../../components/customComponentsV2/vlc/config/vlcConfig";
import { Search } from "@mui/icons-material";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomPaginationV2 from "../../components/customComponentsV2/common/pagination/CustomPaginationV2";

import VlcCardHeader from "../../components/customComponentsV2/vlc/cards/VlcCardHeader";
import { componentStyle } from "../../config/appTheme";
import VlcCardContent from "../../components/customComponentsV2/vlc/cards/VlcCardContent";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../components/customComponentsV2/common/connectors/MultiselectConnectorV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";
import EditDetailsDialog from "../../components/customComponentsV2/vlc/pages/EditDetailsDialog";
import DetailsLogTab from "../../components/customComponentsV2/vlc/pages/DetailsLogTab";
import RequestsAndLogsTabs from "./requestAccess";
import CustomToolTip from "../../components/infoButton/CustomToolTip";
import RecreateSWMDialog from "../../components/customComponentsV2/vlc/pages/RecreateSWMDialog";
import DecisionMatrixPanel from "../legal-requirements/RuleCenter/Executions/DecisionMatrixPanel";
import { showToastMessage } from "../../redux/actions/actionHandler";
import AttributesForm from "./Attributes/AttributesForm";
import AttributeUpdateDialog from "./Attributes/AttributeUpdateDialog";
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 24-07-2024
 * @description : Show the software modules component
 * @param : heading, sWModulesList, handleSWModuleAdd, handleSWModuleUpdate, handleSWModuleReplace, setIsEEComponentSelected, setSelectedEEComponentDetails, selectedProjectDetails,
 * @return : The rendered software module component.
 */
function VlcSWModules({
  selectedVariantsMany,
  variantOptionList,
  heading,
  sWModulesList,
  handleSWModuleAdd,
  handleSWModuleUpdate,
  handleSWModuleReplace,
  setIsEEComponentSelected,
  setSelectedEEComponentDetails,
  selectedProjectDetails,
  selectedEEComponentDetails,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  getSWModulesList,
  selectedSingleVariantDetails,
  swmSearchFieldValue,
  setSWMSearchFieldValue,
  swmSelectedSearchField,
  setSWMSelectedSearchField,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalSWMCount,
  hasMore,
  setHasMore,
  swmNameFilter,
  setSWMNameFilter,
  swmVersionFilter,
  setSWMVersionFilter,
  swmNameFilterSearch,
  setSWMNameFilterSearch,
  swmVersionFilterSearch,
  setSWMVersionFilterSearch,
  swmSortField,
  setSWMSortField,
  swmSortOrder,
  setSWMSortOrder,
  swmListToFilter,
  filteredSWMList,
  sortedSWMList,
  onRedirectedActionDialogClose,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
    shallowEqual,
  );

  const profileDetails = useSelector((store) => store.profileReducer);

  // Extract role_type from profileDetails
  const roleType = profileDetails?.profile?.role_details?.role_type;

  const supplier = roleType === 2;

  const canEditItem = (item) =>
    item?.is_edit_enabled || selectedProjectDetails?.is_user_project_owner;

  const getActionTooltipTitle = (actionType) => {
    switch (actionType) {
      case 1:
        return "Action required in Software Module to Software Module linking";
      case 2:
        return "Action required in Software Module to Legal requirements";
      case 3:
        return "Action required in this Software module";
      case 4:
        return "Action required in Software Module Attribute";
      case 5:
        return "Action pending in Software Module Version";
      default:
        return "";
    }
  };

  const handleActionTypeClick = (item) => {
    if (item.action_type === 1) {
      handleActionType1DialogOpen(item);
    } else if (item.action_type === 2) {
      handleActionType2DialogOpen(item);
    } else if (item.action_type === 4) {
      setSelectedSingleSWModule(item);
      setSelectedSWModuleUpdateDetails(item);
      setResumeSWMAttributeImpactFlowTrigger((prev) => prev + 1);
    } else if (item.action_type === 5) {
      setSelectedSingleSWModule(item);
      setSelectedSWModuleUpdateDetails(item);
      setOpenSWModuleUpdateDialog(true);
      setResumeSWMVersionImpactFlowTrigger((prev) => prev + 1);
    } else {
      handleActionType3DialogOpen(item);
    }
  };

  const [activeItemId, setActiveItemId] = useState(null); // State to track which item's actions are visible
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");

  /* -------------- Dialogs -------------- */
  const [openSWModuleAddDialog, setOpenSWModuleAddDialog] = useState(false);
  const [openSWModuleUpdateDialog, setOpenSWModuleUpdateDialog] =
    useState(false);
  const [openSWModuleReplaceDialog, setOpenSWModuleReplaceDialog] =
    useState(false);
  const [openSWModuleChangeLogsDialog, setOpenSWModuleChangeLogsDialog] =
    useState(false);
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);
  const [isSWMVersionImpactFlow, setIsSWMVersionImpactFlow] = useState(false);
  const [
    resumeSWMAttributeImpactFlowTrigger,
    setResumeSWMAttributeImpactFlowTrigger,
  ] = useState(0);
  const [
    resumeSWMVersionImpactFlowTrigger,
    setResumeSWMVersionImpactFlowTrigger,
  ] = useState(0);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);

  /* -------------- Selected sw module update/replace details  -------------- */
  const [selectedSWModuleUpdateDetails, setSelectedSWModuleUpdateDetails] =
    useState(null);
  const [selectedSWModuleReplaceDetails, setSelectedSWModuleReplaceDetails] =
    useState(null);
  const [selectedSingleSWModule, setSelectedSingleSWModule] = useState([]);

  /* -------------- Vehicle Functions-------------- */
  const [vfOptionsList, setVfOptionsList] = useState([]);
  const [supplierOptionsList, setSupplierOptionsList] = useState([]);

  /* -------------- SW Modules Change Logs -------------- */
  const [swChangeLogsList, setSWChangeLogsList] = useState([]);
  const [swLogsDialogData, setSWLogsDialogData] = useState({});

  const [requestsDialogOpen, setRequestsDialogOpen] = useState(false);

  const [getRequestsData, setGetRequestData] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const moreBtnRefs = useRef({});

  const [selectedSWM, setSelectedSWM] = useState(null);

  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);
  const [openDialogDraftToAdd, setOpenDialogDraftToAdd] = useState(false);
  const [reasonError, setReasonError] = useState("");

  const linkModuleType = { SWM: 1, EE: 2 };

  const [gitSWMLogsDetails, setGitSWMLogsDetails] = useState([]);

  // Dialog states
  const [openLockUnLockSWMDialog, setOpenLockUnLockSWMDialog] = useState(null);
  const [lockUnLockSWMReason, setLockUnLockSWMReason] = useState(null);
  useState(null);
  const [lockUnLockSWMData, setLockUnLockSWMData] = useState(null);
  const [openGitSWMDialog, setOpenGitSWMDialog] = useState(false);

  // Filtering
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [filteredSWMName, setFilteredSWMName] = useState([]);
  const [filteredSWMVersion, setFilteredSWMVersion] = useState([]);

  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);

  const [openEditDetailsDialog, setOpenEditDetailsDialog] = useState(false);
  const [selectedSwmItem, setSelectedSwmItem] = useState(null);
  const [totalSWMChangeLogCount, setTotalSWMChangeLogCount] = useState(0);
  const [swmSelectedData, setSwmSelectedData] = useState(null);
  const [attributeDecisionMatrixContext, setAttributeDecisionMatrixContext] =
    useState(null);
  const [showAttributeDecisionMatrix, setShowAttributeDecisionMatrix] =
    useState(false);
  const [attributeExecutionId, setAttributeExecutionId] = useState(null);
  const [attributeSimilarTickets, setAttributeSimilarTickets] = useState([]);
  const [attributeSelectedEntity, setAttributeSelectedEntity] = useState(null);
  const [attributeSelectedTicket, setAttributeSelectedTicket] = useState(null);
  const [attributeOpenAiPanel, setAttributeOpenAiPanel] = useState(false);
  const [attributeCreateTicketData, setAttributeCreateTicketData] = useState(
    {},
  );
  const [attributeDecisionMatrixReason, setAttributeDecisionMatrixReason] =
    useState("");
  const [attributeDisabledStandards, setAttributeDisabledStandards] = useState(
    [],
  );
  useEffect(() => {
    dispatch(HandleApiActions({ ...adminSettingsAPI.get_admin_settings }));
  }, []);

  useEffect(() => {
    if (adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.length > 0) {
      const supplierDetails =
        adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.map(
          (data) => ({
            value: data.name,
            label: data.name,
          }),
        );
      setSupplierOptionsList(supplierDetails);
    }
  }, [adminSettingsDetails]);

  useEffect(() => {
    // Use the current list (filtered or not) to populate filter options
    const list = swmListToFilter || sWModulesList || [];
    const names = Array.from(
      new Set(list?.map((item) => item.name).filter(Boolean)),
    )?.map((name) => ({
      value: name,
      label: name,
    }));
    const versions = Array.from(
      new Set(list?.map((item) => item.version).filter(Boolean)),
    )?.map((version) => ({
      value: version,
      label: version,
    }));
    setFilteredSWMName(names);
    setFilteredSWMVersion(versions);
  }, [swmListToFilter, sWModulesList]);

  // Removed extra SWM fetch; filters are derived from the list effect above

  const handleOpenGitHubLogs = async (item) => {
    handleCloseMenu();

    // Open the dialog first
    setOpenGitSWMDialog(true);

    // Prepare request object
    const reqObj = {
      project_id: item?.project_id,
      variant_id: item?.variant_id,
      ee_id: item?.ee_id,
      swm_id: item?.swm_id,
    };
    // const reqObj = {
    //   project_id: 923,
    //   variant_id: 560,
    //   ee_id: 1522,
    //   swm_id: 1585,
    // };

    try {
      const resp = await dispatch(
        HandleApiActions({
          ...githubIntegrationAction.get_git_swm_logs,
          params: reqObj,
        }),
      );
      setGitSWMLogsDetails(resp?.data);
    } catch (error) {
      console.error("Error fetching GitHub SWM Logs:", error);
    }
  };

  // --- GitHub enable state management ---
  const [gitHubEnabledMap, setGitHubEnabledMap] = useState({});

  // useEffect(() => {
  //   // Check GitHub enable status for all items in sWModulesList
  //   const checkGitHubStatus = async () => {
  //     if (!sWModulesList || sWModulesList.length === 0) return;

  //     const newMap = {};

  //     for (const item of sWModulesList) {
  //       try {
  //         // Request object
  //         const reqObj = {
  //           project_id: item?.project_id,
  //           variant_id: item?.variant_id,
  //           ee_id: item?.ee_id,
  //           swm_id: item?.swm_id,
  //         };

  //         // API Call
  //         const resp = await dispatch(
  //           HandleApiActions({
  //             ...githubIntegrationAction.get_git_swm_logs,
  //             params: reqObj,
  //           })
  //         );

  //         // Mark as enabled if data exists
  //         newMap[item.swm_id] = resp?.data?.length > 0;
  //       } catch (error) {
  //         // Mark as disabled on error
  //         newMap[item.swm_id] = false;
  //       }
  //     }

  //     setGitHubEnabledMap(newMap);
  //   };

  //   // ✅ Call function only when list changes
  //   if (sWModulesList && sWModulesList.length > 0) {
  //     checkGitHubStatus();
  //   }
  // }, [sWModulesList, dispatch]);

  // Parent (EEComponents) fetches SWM on EE selection; only reset filters here
  useEffect(() => {
    setFilteredSWMName([]);
    setFilteredSWMVersion([]);
  }, [
    selectedSingleVariantDetails?.variant_id,
    selectedProjectDetails?.project_id,
    selectedEEComponentDetails,
  ]);

  //Effect to refresh list when local paging or search filters change (skip on first mount)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    getSWModulesList();
    // eslint-disable-next-line
  }, [
    page,
    rowsPerPage,
    // selectedSortColumn,
    // selectedSortOrder,
    swmSelectedSearchField,
    swmSearchFieldValue,
  ]);

  useEffect(() => {
    handleFilterResetAll();
    // handleSortResetAll();
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  // Reset the filter values
  const handleFilterResetAll = () => {
    handleFilterChange("name");
  };

  // Handle filter change
  const handleFilterChange = (searchField, searchValue) => {
    setSWMSelectedSearchField(searchField);
    setSWMSearchFieldValue(searchValue);
    setPage(1);
    handleFilterClose();
  };

  // Function to handle filter
  const handleFilterApplyNow = (searchField, searchValue) => {
    handleFilterChange(searchField, searchValue);
  };

  // Open filter menu
  const handleFilterClick = (event) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilter(true);
  };

  // Handle filter close
  const handleFilterClose = () => {
    setAnchorElFilter(null);
    setOpenFilter(false);
  };

  // Handle page change
  const handlePageChange = (selectedPage) => {
    setPage(selectedPage);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  /* Handle lock/un-lock software module with reason dialog */
  const handleLockUnLockSWM = (params) => {
    setLockUnLockSWMData(params);

    setOpenLockUnLockSWMDialog(true);
  };
  const handleConfirmLockUnLockSWM = async () => {
    let apiDetails = labCar.lock_unlock_software_module;

    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails &&
      selectedSingleVariantDetails?.variant_id &&
      selectedEEComponentDetails &&
      selectedEEComponentDetails?.ee_id
    ) {
      customConsole({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(selectedEEComponentDetails?.ee_id),
          swm_id: lockUnLockSWMData?.swmId,
          is_locked: lockUnLockSWMData?.isLocked,
          ...(lockUnLockSWMReason && { reason: lockUnLockSWMReason }),
        },
        show_toast: true,
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            swm_id: lockUnLockSWMData?.swmId,
            is_locked: lockUnLockSWMData?.isLocked,
            ...(lockUnLockSWMReason && { reason: lockUnLockSWMReason }),
          },
          show_toast: true,
        }),
      );
      await getSWModulesList({ force: true });
      handleCloseLockUnLockSWM();
      // if (resp.error) {
      // } else {
      //   // getSWModulesList();
      // }
    }
  };
  const handleCloseLockUnLockSWM = () => {
    setOpenLockUnLockSWMDialog(false);

    // Delay clearing the reason by 300 milliseconds
    setTimeout(() => {
      setLockUnLockSWMData(null);
      setLockUnLockSWMReason("");
    }, 300);
  };

  /* -------------- Handle update/replace functions -------------- */
  const handleSelectedSWModuleUpdateDetails = (sWModule) => {
    setOpenSWModuleUpdateDialog(true);

    if (sWModule) {
      setSelectedSWModuleUpdateDetails(sWModule);
    }
  };

  const handleSelectedSWModuleReplaceDetails = (sWModule) => {
    setOpenSWModuleReplaceDialog(true);

    if (sWModule) {
      setSelectedSWModuleReplaceDetails(sWModule);
    }
  };

  const handleSelectedDetailsLogDialog = (sWModule) => {
    setOpenEditDetailsDialog(true);


  };

  /* Get software module change logs */
  const handleGetSWModuleChangeLogs = async ({ swm_id, pageNo, pageSize }) => {
    let apiDetails = labCar.get_sw_modules_change_logs;

    if (
      selectedProjectDetails &&
      selectedProjectDetails?.project_id &&
      // selectedProjectDetails?.variant_id &&
      selectedEEComponentDetails &&
      selectedEEComponentDetails?.ee_id
    ) {
      customConsole({
        project_id: Number(selectedProjectDetails?.project_id),
        ee_id: Number(selectedEEComponentDetails?.ee_id),
        swm_id,
        page_number: pageNo,
        page_size: pageSize,
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            swm_id,
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      );
      if (resp.error) {
        setSWChangeLogsList([]);
      } else {
        setSWChangeLogsList(resp?.data);
        setTotalSWMChangeLogCount(resp?.total_count);
      }
    }
  };

  /* Handle dialogs */
  const handleSWLogsDialogOpen = (data) => {
    setOpenSWModuleChangeLogsDialog(true);

    // handleGetSWModuleChangeLogs({
    //   ee_id: Number(data?.ee_id),
    //   swm_id: Number(data?.swm_id),
    // });

    setSWLogsDialogData(data);
  };
  const handleSWLogsDialogClose = () => {
    setSWLogsDialogData(null);
    setSWChangeLogsList([]);

    setOpenSWModuleChangeLogsDialog(false);
  };

  /* -------------- useEffects -------------- */
  useEffect(() => {

    if (
      selectedProjectDetails &&
      selectedProjectDetails?.vehicle_function_details
    ) {
      setVfOptionsList(selectedProjectDetails?.vehicle_function_details || []);
    }
  }, [selectedProjectDetails]);

  useEffect(() => {
    customConsole(
      "selectedSWModuleUpdateDetails: " + selectedSWModuleUpdateDetails,
    );
    customConsole(
      "selectedSWModuleReplaceDetails: " + selectedSWModuleReplaceDetails,
    );
  }, [selectedSWModuleUpdateDetails, selectedSWModuleReplaceDetails]);

  // const handleMoreClick = (e, itemId) => {
  //   e.stopPropagation(); // Prevent click event from bubbling up
  //   setActiveItemId(activeItemId === itemId ? null : itemId); // Toggle visibility
  // };

  const handleCloseActions = () => {
    setActiveItemId(null); // Close all actions
  };

  // Modify the dialog open handler to set the selected software module
  const handleDialogOpen = (swm) => {
    setSelectedSWM(swm); // Store the selected module
    setDialogOpen(true); // Open the dialog
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setReason(""); // Reset reason
  };

  const handleSubmit = () => {
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      swm_id: Number(selectedSWM?.swm_id),
      request_reason: reason,
    };

    dispatch(
      HandleApiActions({
        ...editRequestAPI.create_edit_requests,
        params: reqObj,
        show_toast: true,
      }),
    );
    // Reset form fields
    setReason("");
    handleDialogClose();
  };

  const handleActionType3DialogOpen = (swm) => {
    setSelectedSWM(swm);
    setPendingDialogOpen(true);
  };

  const handlePendingDialogClose = () => {
    setPendingDialogOpen(false);
  };

  const pendingActionRequired = async () => {
    // Create the request object
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      swm_id: Number(selectedSWM?.swm_id),
      is_dependency_linking: true,
      module_type: "MODULE LINKING",
    };
    try {
      // Dispatch the update action type API action
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: reqObj,
          show_toast: false,
        }),
      );
      await getSWModulesList({ force: true });
      // Handle successful edit
      // if (!response.error) {
      handlePendingDialogClose();
      // }
    } catch (error) {
      console.error("Failed to update action type", error);
    }
  };

  const handleMoreClick = (e, item) => {
    if (e?.stopPropagation) e.stopPropagation();
    const anchor =
      e?.currentTarget || moreBtnRefs.current?.[item?.swm_id] || null;
    setAnchorEl(anchor);
    setActiveItemId(item.swm_id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveItemId(null);
  };

  const handleRequestsDialogOpen = (item) => {
    setSwmSelectedData(item);
    setSelectedSWM(item);
    setRequestsDialogOpen(true);
    getRequests(item);
  };

  const handleRequestsDialogClose = () => {
    setRequestsDialogOpen(false);
  };

  const handleMenuAction = (action, item) => {
    handleCloseMenu(); // Close the menu after action
    if (action === "update") {
      handleSelectedSWModuleUpdateDetails(item);
    } else if (action === "replace") {
      handleSelectedSWModuleReplaceDetails(item);
    } else if (action === "logs") {
      setSelectedSwmItem(item);
      handleSWLogsDialogOpen(item);
      // handleSelectedSWModuleReplaceDetails(item);
    } else if (action === "requests") {
      handleRequestsDialogOpen(item);
    } else if (action === "details") {
      setDetailsItem(item); // <-- This updates detailsItem
      setOpenDetailsDialog(true); // <-- This opens the dialog
    } else if (action === "updateAttribute") {
      // Handle update attribute action
      setSelectedSingleSWModule(item);
      setSelectedSWModuleUpdateDetails(item);
      setOpenAttributeUpdateDialog(true);
    }
  };

  const isAttributeDecisionMatrixContextValid = (data) => {
    if (!data?.standards || data.standards.length === 0) {
      return false;
    }

    return data.standards.some((standard) => {
      if (!standard.rule_sets || standard.rule_sets.length === 0) {
        return false;
      }

      return standard.rule_sets.some(
        (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
      );
    });
  };

  const loadAttributeDecisionMatrixContext = async () => {
    const targetSWM = selectedSWModuleUpdateDetails || selectedSingleSWModule;

    if (
      !targetSWM?.swm_id ||
      !selectedProjectDetails?.project_id ||
      !selectedSingleVariantDetails?.variant_id
    ) {
      return;
    }

    const standardsRes = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.decision_matrix_get_context,
        params: {
          component_type: "SWM",
          component_id: targetSWM.swm_id,
          project_id: selectedProjectDetails.project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
        },
        show_toast: false,
      }),
    );

    const contextData = standardsRes?.data;

    if (!isAttributeDecisionMatrixContextValid(contextData)) {
      setAttributeDecisionMatrixContext(null);
      setShowAttributeDecisionMatrix(false);
      return;
    }

    setAttributeDecisionMatrixContext(contextData);
    setAttributeDecisionMatrixReason(reason?.trim() || "");
    setShowAttributeDecisionMatrix(true);
  };

  const handleAttributeDecisionMatrixClose = () => {
    setShowAttributeDecisionMatrix(false);
    setAttributeDecisionMatrixContext(null);
    setAttributeExecutionId(null);
    setAttributeSimilarTickets([]);
    setAttributeSelectedEntity(null);
    setAttributeSelectedTicket(null);
    setAttributeOpenAiPanel(false);
    setAttributeCreateTicketData({});
    setAttributeDecisionMatrixReason("");
    setAttributeDisabledStandards([]);
  };

  const handleAttributeExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_start,
          params: {
            workflowId: payload.workflowId,
            version: payload.version,
          },
          show_toast: false,
        }),
      );

      setAttributeExecutionId(res.executionId);
      return res;
    }

    if (mode === "COMPLETE") {
      return dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_node_complete,
          params: {
            executionId: payload.executionId,
            nodeId: payload.nodeId,
            input: payload.input,
          },
          show_toast: false,
        }),
      );
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleAttributeViewExecution = (executionId) => {
  };

  const handleAttributeDecisionMatrixAction = async ({ standardCode } = {}) => {
    const res = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.re_create_ticket_v1,
        params: {
          details: attributeCreateTicketData,
          ...(attributeDecisionMatrixReason && {
            reason: attributeDecisionMatrixReason,
          }),
        },
        show_toast: true,
      }),
    );

    if (!res?.error && standardCode) {
      setAttributeDisabledStandards((prev) => [
        ...new Set([...prev, standardCode]),
      ]);
    }

    return res;
  };

  const handelGetAttributeSimilerTickets = async ({ standard_name } = {}) => {
    const targetSWM = selectedSWModuleUpdateDetails || selectedSingleSWModule;

    const res = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.get_similar_tickets,
        params: {
          execution_id: attributeExecutionId,
          entity_type: "swm",
          entity_id: Number(targetSWM?.swm_id),
          ...(standard_name != null ? { standard_name } : {}),
        },
        show_toast: true,
      }),
    );

    setAttributeSimilarTickets(res?.data || []);
    setAttributeCreateTicketData(res?.data || []);
    return res;
  };

  const handelGetAttributeAiSuggestion = (ticket) => {
    setAttributeSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "Software Module",
    });
    setAttributeOpenAiPanel(true);
    setAttributeSelectedTicket(ticket);
  };

  const handlePendingDialogSuccess = () => {
    handlePendingDialogClose();
    const targetItem = selectedSWM || swmSelectedData;
    if (targetItem) {
      const anchor =
        moreBtnRefs.current?.[targetItem?.swm_id] ||
        (typeof document !== "undefined"
          ? document.querySelector(`[data-swm-id="${targetItem?.swm_id}"]`)
          : null);
      if (anchor) {
        setAnchorEl(anchor);
        setActiveItemId(targetItem.swm_id);
      }
    }
  };



  // Details dialog
  const DetailRow = ({ label, value }) => (
    <Box display="flex" gap={1} mb={1}>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ minWidth: "130px", color: "#333" }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );

  const getRequests = async (item) => {
    let apiDetails = editRequestAPI?.get_edit_requests;
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(selectedEEComponentDetails?.ee_id),
          swm_id: item?.swm_id,
          is_sent_requests: true,
          page_number: 1,
          page_size: 25,
        },
        show_toast: false,
      }),
    );
    if (resp.error) {
      setGetRequestData([]);
    } else {
      setGetRequestData(resp.data);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 100:
        return "Pending";
      case 200:
        return "Approved";
      case 300:
        return "Rejected";
      default:
        return "Unknown Status";
    }
  };

  const handleActionType1DialogOpen = (swm) => {
    setSelectedSWM(swm);
    setActionType1DialogOpen(true);
  };

  const handleActionType2DialogOpen = (swm) => {
    setSelectedSWM(swm);
    setActionType2DialogOpen(true);
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  // recreate functions

  const getParentSWMDetails = async () => {
    // api call for getting parent EE components

    let parentSWMResponse = await dispatch(
      HandleApiActions({
        ...labCar.get_parent_swm,
        params: {
          project_id: selectedProjectDetails.project_id,
          parent_project_id: selectedProjectDetails.parent_project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
          parent_variant_id:
            selectedSingleVariantDetails?.parent_variant_details?.variant_id,
          ee_id: selectedEEComponentDetails.ee_id,
          parent_ee_id: selectedEEComponentDetails?.parent_ee_id,
        },
      }),
    );
    if (!parentSWMResponse.error) {
      setSelectedParentSWMDetails(parentSWMResponse.data[0]);
      setRecreationParentSWMList(parentSWMResponse.data);
      setOpenRecreationSWMDialog(true);
    } else {
      showToastMessage({
        type: "error",
        message: parentSWMResponse.msg,
      });
      setRecreationParentSWMList([]);
      setSelectedParentSWMDetails(null);
    }
  };

  const handleSWMRecreateOpen = async () => {
    await getParentSWMDetails();
  };

  const zones = [
    "Central Left",
    "CENTRAL RIGHT",
    "FRONT",
    "REAR",
    "FRONT LEFT",
    "FRONT RIGHT",
    "REAR LEFT",
    "REAR RIGHT",
  ];

  const zoneMapping = {
    "CENTRAL LEFT": 0,
    "CENTRAL RIGHT": 1,
    FRONT: 2,
    REAR: 3,
    "FRONT LEFT": 4,
    "FRONT RIGHT": 5,
    "REAR LEFT": 6,
    "REAR RIGHT": 7,
  };

  // Log the zone_position_name

  // Use the mapping to get the index
  const selectedZoneIndex = zoneMapping[selectedZone?.zone_position_name];
  const selectedZoneName =
    selectedZoneIndex !== undefined ? zones[selectedZoneIndex] : null;


  const isSWMFilterActive =
    (swmNameFilter && swmNameFilter?.length > 0) ||
    (swmVersionFilter && swmVersionFilter?.length > 0) ||
    (swmNameFilterSearch && swmNameFilterSearch?.length > 0) ||
    (swmVersionFilterSearch && swmVersionFilterSearch?.length > 0) ||
    (swmSortField && swmSortField !== "name") ||
    (swmSortOrder && swmSortOrder !== "asc");

  const [swmNameSearch, setSwmNameSearch] = useState("");
  // recreate states
  const [openRecreationSWMDialog, setOpenRecreationSWMDialog] = useState(false);
  const [selectedParentSWMDetails, setSelectedParentSWMDetails] =
    useState(null);
  const [recreationParentSWMList, setRecreationParentSWMList] = useState([]);

  const debounceRef = useRef();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSWMSelectedSearchField("name"); // Always search by name
      setSWMSearchFieldValue(swmNameSearch);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [swmNameSearch]);

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      tickets_type: 1,
    };

    try {
      // First API call
      const response = await dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets,
          params: reqObj,
          show_toast: false,
        }),
      );

      // Extract and deduplicate standards
      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              ?.map((ticket) => ticket?.ls_details?.standard_number)
              ?.filter((std) => Boolean(std))
            : [],
        ),
      ];

      if (standardsWithRuleSet?.length > 0) {
        // Second API call with standards
        const standardWithRuleSet = await dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_engine_with_standards,
            params: { standard: standardsWithRuleSet },
            show_toast: false,
          }),
        );
        return standardWithRuleSet?.data || [];
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch standard with ruleset", error);
      return false;
    }
  };

  // Truncate long headings and append ellipsis
  const formatHeading = (text, max = 30) => {
    const fallback = "E/E Not Available";
    if (!text) return fallback;
    const str = String(text);
    return str?.length > max ? str.slice(0, max - 3) + "..." : str;
  };
  const vlcHeaderTitle = formatHeading(heading || "E/E Not Available", 30);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%" }}>
        {/* Software module card */}
        <Card

          sx={{
            ...vlcComponentStyle.card,
            display: "flex",
            flexDirection: "column",
            height: "450px",
          }}
        >
          <CardHeader

            sx={vlcComponentStyle.cardHeader}
            title={
              <VlcCardHeader
                title={vlcHeaderTitle}
                showRecreateIcon={
                  selectedProjectDetails?.parent_project_id &&
                    selectedSingleVariantDetails?.parent_variant_details
                      ?.variant_id &&
                    selectedEEComponentDetails?.parent_ee_id
                    ? true
                    : false
                }
                onRecreateClick={handleSWMRecreateOpen}
                selectedEEComponentDetails={selectedEEComponentDetails}
                variantOptionList={variantOptionList}
                selectedSingleVariantDetails={selectedSingleVariantDetails}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  sx={{
                    width: "100%",
                    mt: 1,
                  }}
                >

                  <TextField
                    size="small"
                    placeholder="Search Number"
                    // label="Search Software Modules"
                    variant="outlined"
                    fullWidth
                    sx={componentStyle.searchField.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),

                    }}
                    value={swmNameSearch}
                    onChange={(e) => setSwmNameSearch(e.target.value)}
                  />
                  <Box>
                    {!supplier ? (
                      <AddButtonV2
                        disabled={
                          !selectedEEComponentDetails?.ee_id ||
                          selectedEEComponentDetails?.status === false
                          // !selectedVariantsMany || !variantOptionList?.length
                        }
                        handleOnClick={() => setOpenSWModuleAddDialog(true)}
                      />
                    ) : null}
                  </Box>
                  {/* </Box> */}
                </Box>
              </VlcCardHeader>
            }
          />
          {/* Software modules list */}
          <CardContent
            sx={{
              ...vlcCardStyles.CardContent,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 0,
              scrollbarColor: "#888 #f1f1f1",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            {sWModulesList?.length > 0 && heading ? (
              sWModulesList?.map((item, index) => (
                <VlcCardContent
                  onClick={() => { }}
                  data={item}
                  key={item.swm_id}
                  isSelected={false}
                >
                  <Box
                    key={index}
                    // sx={styles.cardItems}
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                    }}
                  >
                    <Box
                      // sx={styles.cardItemsTxtContainer}
                      sx={{
                        width: "70%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start", // ✅ ensures left alignment
                          p: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            // fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            // fontWeight: 500,
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          <CustomToolTip
                            type="string"
                            length={60}
                            style={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              fontWeight: "bold",
                            }}
                            title={`Number: ${item?.name ?? ""}`}
                          >
                            {`Number: ${item?.name ?? ""}`}
                          </CustomToolTip>
                        </Typography>
                        <Typography
                          sx={{
                            // fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            // fontWeight: 500,
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            color: vlcCardStyles.vlcCardListColor,
                          }}
                        >
                          <CustomToolTip
                            type="string"
                            length={60}
                            style={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              // fontWeight: "bold",
                            }}
                            title={`Description: ${item?.description ?? ""}`}
                          >
                            {`Description: ${item?.description ?? ""}`}
                          </CustomToolTip>
                        </Typography>
                        <Typography
                          sx={{
                            // fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            // fontWeight: 500,
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            color: vlcCardStyles.vlcCardListColor,
                          }}
                        >
                          <CustomToolTip
                            type="string"
                            length={60}
                            style={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              // fontWeight: "bold",
                            }}
                            title={`Version: ${item?.version ?? ""}`}
                          >
                            {`Version: ${item?.version ?? ""}`}
                          </CustomToolTip>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          ml: 0.5,
                          mt: -0.5,
                        }}
                      >
                        {item?.status === false ? (
                          <Box sx={{ pl: 0.5, pb: 0.5 }}>
                            <Chip
                              onClick={() => {
                                setOpenDialogDraftToAdd(true);
                                setSelectedSingleSWModule(item);
                              }}
                              sx={{
                                borderRadius: 0,
                                color: "#007CB0",
                                borderColor: "#007CB0",
                                borderWidth: 1,
                                // fontWeight: "bold",
                                fontSize: "11px",
                              }}
                              size="small"
                              variant="outlined"
                              label={"Draft"}
                            />
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        key={item?.swm_id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-start"
                        // gap={0.1}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            // gap={0.1}
                            sx={{
                              flexWrap: "nowrap",
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            {/* {item?.meta_details ? (
                              <IconButton
                                size="small"
                                onClick={() => handleOpenGitHubLogs(item)}
                              >
                                <GitHubIcon />
                              </IconButton>
                            ) : null} */}
                            {item.action_type !== 0 && item.status === true && (
                              <CustomToolTip
                                type="component"
                                title={getActionTooltipTitle(item.action_type)}
                              >
                                <IconButton
                                  size="small"
                                  sx={{ color: "black" }}
                                  onClick={() => handleActionTypeClick(item)}
                                >
                                  <ErrorOutlineIcon sx={{ fontSize: "18px" }} />
                                </IconButton>
                              </CustomToolTip>
                            )}
                            {/* Lock/UnLock Icons */}
                            {item?.is_locked ? (
                              <CustomToolTip type="component" title="Lock">
                                <IconButton
                                  size="small"
                                  sx={{ color: "black" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleLockUnLockSWM({
                                      swmId: item?.swm_id,
                                      isLocked: false,
                                    });
                                  }}
                                >
                                  <LockIcon sx={{ fontSize: "18px" }} />
                                </IconButton>
                              </CustomToolTip>
                            ) : (
                              <CustomToolTip type="component" title="UnLock">
                                <IconButton
                                  size="small"
                                  sx={{ color: "black" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleLockUnLockSWM({
                                      swmId: item?.swm_id,
                                      isLocked: true,
                                    });
                                  }}
                                >
                                  <NoEncryptionGmailerrorredIcon
                                    sx={{ fontSize: "18px" }}
                                  />
                                </IconButton>
                              </CustomToolTip>
                            )}
                            {/* More */}
                            <CustomToolTip type="component" title="More">
                              <IconButton
                                size="small"
                                sx={{ color: "black" }}
                                data-swm-id={item?.swm_id}
                                ref={(el) => {
                                  if (el)
                                    moreBtnRefs.current[item?.swm_id] = el;
                                }}
                                onClick={(e) => handleMoreClick(e, item)}
                              >
                                <MoreVertIcon sx={{ fontSize: "18px" }} />
                              </IconButton>
                            </CustomToolTip>
                          </Box>
                        </Box>

                        {/* Menu with conditional editing options */}
                        <Menu
                          anchorEl={anchorEl}
                          open={
                            Boolean(anchorEl) && activeItemId === item?.swm_id
                          }
                          onClose={handleCloseMenu}
                          PaperProps={{
                            sx: {
                              borderRadius: 0, // ✅ remove rounding completely
                              boxShadow: 1, // optional: remove shadow if you want a flat look
                            },
                          }}
                        >
                          {!item?.is_locked && canEditItem(item) ? (
                            <>
                              {/* <MenuItem
                                onClick={() =>
                                  handleMenuAction("details", item)
                                }
                                sx={{ ...componentStyle.font.cardText }}
                              >
                                Details
                              </MenuItem> */}
                              {/* <MenuItem
                                onClick={() => handleMenuAction("update", item)}
                                sx={{ ...componentStyle.font.cardText }}
                              >
                                Update
                              </MenuItem> */}
                              {/* <MenuItem
                                onClick={() =>
                                  handleMenuAction("replace", item)
                                }
                                sx={{ ...componentStyle.font.cardText }}
                              >
                                Replace
                              </MenuItem> */}
                              {item?.action_type === 0 ||
                                item?.action_type === 3 ? (
                                <MenuItem
                                  onClick={() => {
                                    setOpenEditDetailsDialog(true);
                                    setSelectedSwmItem(item);
                                  }}
                                  sx={{ ...componentStyle.font.cardText }}
                                >
                                  Edit Details
                                </MenuItem>
                              ) : null}
                            </>
                          ) : (
                            <>
                              {/* <MenuItem
                                onClick={() =>
                                  handleMenuAction("details", item)
                                }
                                sx={{ ...componentStyle.font.cardText }}
                              >
                                Details
                              </MenuItem>
                              <MenuItem disabled>Update</MenuItem>
                              <MenuItem disabled>Replace</MenuItem> */}
                            </>
                          )}
                          {!canEditItem(item) && (
                            <MenuItem
                              onClick={() => handleMenuAction("requests", item)}
                              sx={{ ...componentStyle.font.cardText }}
                            >
                              Edit Request
                            </MenuItem>
                          )}
                          <MenuItem
                            onClick={() => handleMenuAction("logs", item)}
                            sx={{ ...componentStyle.font.cardText }}
                          >
                            Log
                          </MenuItem>
                          {item?.meta_details && (
                            <MenuItem
                              onClick={() => handleOpenGitHubLogs(item)}
                              sx={{ ...componentStyle.font.cardText }}
                            >
                              GitHub
                            </MenuItem>
                          )}
                        </Menu>
                      </Box>
                    </Box>
                  </Box>
                </VlcCardContent>
              ))
            ) : (
              <Typography variant="body1" sx={styles.noDataAvailableTxt}>
                No data available
              </Typography>
            )}
          </CardContent>
          {/* <CardActions sx={styles.cardActions}>
            <Button
              startIcon={<CloseIcon />}
              color="error"
              size="small"
              variant="contained"
              onClick={() => {
                setIsEEComponentSelected(false);
                setSelectedEEComponentDetails(null);
              }}
              sx={{ ...appTheme.commonBtnStyle }}
            >
              Close
            </Button>
          </CardActions> */}
          <Paper
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              // p: { xs: "2px", sm: "3px" },
              minHeight: "40px",
              borderTop: "1px solid #ddd",
              borderRadius: "0px !important",
            }}
          >
            {/* <CustomPagination
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              hasMore={hasMore}
            /> */}
            {totalSWMCount > 0 && (
              <CustomPaginationV2
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                totalItems={totalSWMCount}
              />
            )}
          </Paper>
        </Card>
      </Box>

      {/* Lock and Un Lock EE dialoug */}
      <CustomDialogBoxV2
        open={openLockUnLockSWMDialog}
        onClose={handleCloseLockUnLockSWM}
        title={`Reason to ${lockUnLockSWMData?.isLocked ? "Lock" : "UnLock"}`}
        successButtonName={"Confirm"}
        rejectButtonName={"Cancel"}
        onClickSuccess={handleConfirmLockUnLockSWM}
        onClickReject={handleCloseLockUnLockSWM}
        actionButtonStyle={"continuous"}
        onDisabled={lockUnLockSWMReason?.length < 3}
      >
        <Stack spacing={2} sx={{ width: "350px" }}>
          <CustomFormTextFieldV2
            label="Reason"
            value={lockUnLockSWMReason}
            setValue={setLockUnLockSWMReason}
            // txtFldStyle={{ width: "270px" }}
            required={true}
          />
        </Stack>
      </CustomDialogBoxV2>

      {/* <CustomConfirmationDialog
        open={openLockUnLockSWMDialog}
        onClose={handleCloseLockUnLockSWM}
        dialogTitle={`Reason to ${
          lockUnLockSWMData?.isLocked ? "Lock" : "UnLock"
        } SWM`}
        dialogContent={
          <Box sx={{ mt: 1.4 }}>
            <CustomFormTextFieldV2
              size={"small"}
              placeholder={"Reason"}
              type={"text"}
              value={lockUnLockSWMReason}
              setValue={setLockUnLockSWMReason}
            />
          </Box>
        }
        onConfirm={handleConfirmLockUnLockSWM}
      /> */}

      <EditDetailsDialog
        openEditDetails={openEditDetailsDialog}
        setOpenEditDetails={setOpenEditDetailsDialog}
        onClick={(type) => {
          setOpenEditDetailsDialog(false);
          handleMenuAction(type, selectedSwmItem);
        }}
      />

      <GithubSWMLogs
        gitSWMLogsDetails={gitSWMLogsDetails}
        setGitSWMLogsDetails={setGitSWMLogsDetails}
        openGitSWMDialog={openGitSWMDialog}
        setOpenGitSWMDialog={setOpenGitSWMDialog}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        handleSWModuleUpdate={handleSWModuleUpdate}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        selectedSWM={selectedSWM}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        checkForStandardWithRuleSet={checkForStandardWithRuleSet}
        sWModulesList={sWModulesList}
      />
      <VlcSWModuleAddDialog
        supplierOptionsList={supplierOptionsList}
        selectedVariantsMany={selectedVariantsMany}
        getSWModulesList={getSWModulesList}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleAddDialog={openSWModuleAddDialog}
        setOpenSWModuleAddDialog={setOpenSWModuleAddDialog}
        handleSWModuleAdd={handleSWModuleAdd}
        vfOptionsList={vfOptionsList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
      />
      <VlcSWModuleUpdateDialog
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleUpdateDialog={openSWModuleUpdateDialog}
        setOpenSWModuleUpdateDialog={setOpenSWModuleUpdateDialog}
        handleSWModuleUpdate={handleSWModuleUpdate}
        selectedSWModuleUpdateDetails={selectedSWModuleUpdateDetails}
        setSelectedSWModuleUpdateDetails={setSelectedSWModuleUpdateDetails}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        selectedSWM={selectedSWM}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        isSWMVersionImpactFlow={isSWMVersionImpactFlow}
        setIsSWMVersionImpactFlow={setIsSWMVersionImpactFlow}
        resumeSWMAttributeImpactFlowTrigger={
          resumeSWMAttributeImpactFlowTrigger
        }
        resumeSWMVersionImpactFlowTrigger={resumeSWMVersionImpactFlowTrigger}
        onRedirectedActionDialogClose={onRedirectedActionDialogClose}
      />
      {/* For Draft */}
      <VlcSWModuleReplaceDialog
        supplierOptionsList={supplierOptionsList}
        title={"Add Draft Software Module"}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleReplaceDialog={openDialogDraftToAdd}
        setOpenSWModuleReplaceDialog={setOpenDialogDraftToAdd}
        handleSWModuleReplace={handleSWModuleReplace}
        selectedSWModuleReplaceDetails={selectedSingleSWModule}
        setSelectedSWModuleReplaceDetails={setSelectedSWModuleReplaceDetails}
        vfOptionsList={vfOptionsList}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
      />
      {/* For Replace */}
      <VlcSWModuleReplaceDialog
        supplierOptionsList={supplierOptionsList}
        title={"Replace Software Module"}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleReplaceDialog={openSWModuleReplaceDialog}
        setOpenSWModuleReplaceDialog={setOpenSWModuleReplaceDialog}
        handleSWModuleReplace={handleSWModuleReplace}
        selectedSWModuleReplaceDetails={selectedSWModuleReplaceDetails}
        setSelectedSWModuleReplaceDetails={setSelectedSWModuleReplaceDetails}
        vfOptionsList={vfOptionsList}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
      />

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openAttributeUpdateDialog}
        setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenAttributeUpdateDialog(false);
          setIsSWMVersionImpactFlow(false);
          onRedirectedActionDialogClose?.();
        }}
        onClickSuccess={() => {
          getSWModulesList({ force: true });
          setOpenAttributeUpdateDialog(false);
        }}
        onShowUpdateDialog={() => {
          setIsSWMVersionImpactFlow(true);
          setSelectedSWModuleUpdateDetails(selectedSingleSWModule);
          getSWModulesList({ force: true });
          setTimeout(() => {
            setOpenSWModuleUpdateDialog(true);
          }, 300);
        }}
        selectedComponent={selectedSingleSWModule}
        componentType={"swm"}
        variantId={selectedSingleVariantDetails?.variant_id}
        onIntersectionNo={loadAttributeDecisionMatrixContext}
        reason={reason}
        setReason={setReason}
        reasonError={reasonError}
      />

      <Dialog
        open={showAttributeDecisionMatrix}
        onClose={handleAttributeDecisionMatrixClose}
        fullScreen
        PaperProps={{
          sx: {
            width: "100vw",
            height: "100vh",
            margin: 0,
            borderRadius: 0,
          },
        }}
      >
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: "#26890D",
          }}
        >
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Decision Matrix
            </Typography>
            <IconButton
              color="inherit"
              onClick={handleAttributeDecisionMatrixClose}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flex: 1,
            height: "calc(100vh - 64px)",
            overflow: "auto",
            p: 2,
          }}
        >
          {showAttributeDecisionMatrix && (
            <DecisionMatrixPanel
              context={attributeDecisionMatrixContext}
              key={showAttributeDecisionMatrix ? "open" : "closed"}
              onExecuteWorkflow={handleAttributeExecuteWorkflow}
              onViewExecution={handleAttributeViewExecution}
              onClose={handleAttributeDecisionMatrixClose}
              onDecisionMatrixAction={handleAttributeDecisionMatrixAction}
              handelGetSimilerTickets={handelGetAttributeSimilerTickets}
              similarTickets={attributeSimilarTickets}
              setSimilarTickets={setAttributeSimilarTickets}
              entityDetails={attributeSelectedEntity}
              handelGetAiSuggestion={handelGetAttributeAiSuggestion}
              selectedTicket={attributeSelectedTicket}
              setSelectedTicket={setAttributeSelectedTicket}
              openAiPanel={attributeOpenAiPanel}
              setOpenAiPanel={setAttributeOpenAiPanel}
              setCreateTicketData={setAttributeCreateTicketData}
              disabledStandards={attributeDisabledStandards}
            />
          )}
        </Box>
      </Dialog>

      {/* RECREATE dialog */}
      <RecreateSWMDialog
        supplierOptionsList={supplierOptionsList}
        title={"Recreate Software Module"}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleReplaceDialog={openRecreationSWMDialog}
        setOpenSWModuleReplaceDialog={setOpenRecreationSWMDialog}
        handleSWModuleReplace={handleSWModuleReplace}
        selectedSWModuleReplaceDetails={selectedParentSWMDetails}
        setSelectedSWModuleReplaceDetails={setSelectedSWModuleReplaceDetails}
        vfOptionsList={vfOptionsList}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        recreationParentSWMList={recreationParentSWMList}
        setRecreationParentSWMList={setRecreationParentSWMList}
        selectedParentSWMDetails={selectedParentSWMDetails}
        setSelectedParentSWMDetails={setSelectedParentSWMDetails}
      />
      {/* <CustomLogsDialog
        open={openSWModuleChangeLogsDialog}
        handleClose={handleSWLogsDialogClose}
        dialogTitle={"Change Logs"}
        dialogContent={swChangeLogsList}
        data={swLogsDialogData}
        getLogsData={handleGetSWModuleChangeLogs}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
      /> */}

      <CustomDialogBoxV2
        open={openSWModuleChangeLogsDialog}
        onClose={handleSWLogsDialogClose}
        title={"Software Modules"}
      >
        <Box sx={{ width: "450px", height: "500px" }}>
          {/* <SWMForm
            supplierOptionsList={supplierOptionsList}
            title={"Replace SW Module"}
            selectedVariantsMany={selectedVariantsMany}
            variantOptionList={variantOptionList}
            selectedProjectDetails={selectedProjectDetails}
            selectedEEComponentDetails={selectedEEComponentDetails}
            openSWModuleReplaceDialog={openEditDetailsDialog}
            setOpenSWModuleReplaceDialog={setOpenEditDetailsDialog}
            handleSWModuleReplace={handleSWModuleReplace}
            selectedSWModuleReplaceDetails={selectedSWModuleReplaceDetails}
            setSelectedSWModuleReplaceDetails={
              setSelectedSWModuleReplaceDetails
            }
            vfOptionsList={vfOptionsList}
            setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
            selectedZone={selectedZone}
            getSWModulesList={getSWModulesList}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
          /> */}
          <DetailsLogTab
            logData={swLogsDialogData}
            ChangeLogsList={swChangeLogsList}
            getLogsData={handleGetSWModuleChangeLogs}
            selectedComponent={selectedSwmItem}
            zone_name={selectedEEComponentDetails?.zone_details?.zone_name}
            title={"Update Draft SWM Module"}
            totalLogsCount={totalSWMChangeLogCount}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEditDetailsDialog={openSWModuleChangeLogsDialog}
            setOpenEditDetailsDialog={setOpenSWModuleChangeLogsDialog}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            selectedVariantsMany={selectedVariantsMany}
            handleSWModuleReplace={handleSWModuleReplace}
            selectedSWModuleReplaceDetails={selectedSwmItem}
            setSelectedSWModuleReplaceDetails={setSelectedSwmItem}
            vfOptionsList={vfOptionsList}
            type={"SWM"}
          />
        </Box>
      </CustomDialogBoxV2>
      <Box>

        <CustomDialogBoxV2
          open={requestsDialogOpen}
          onClose={handleRequestsDialogClose}
          title="Edit Requests"
        // maxWidth="sm"
        // paperSx={{ width: "400px" }}
        >
          <Box sx={{ width: "450px", height: "300px" }}>
            <RequestsAndLogsTabs
              getRequestsData={getRequestsData}
              getStatusLabel={getStatusLabel}
              reason={reason}
              setReason={setReason}
              reasonError={reasonError}
              setReasonError={setReasonError}
              handleSubmit={handleSubmit}
              onClose={handleRequestsDialogClose}
              selectedProjectDetails={selectedProjectDetails}
              data={swmSelectedData}
            />
          </Box>
        </CustomDialogBoxV2>

        {/* Action Pending Dialog */}

        <CustomDialogBoxV2
          open={pendingDialogOpen}
          onClose={handlePendingDialogClose}
          title={"Action Required"}
          onClickSuccess={handlePendingDialogSuccess} // Close and open More menu
          onClickReject={pendingActionRequired}
          actionButtonStyle={"continuous"}
          successButtonName={"Yes"}
          rejectButtonName={"No"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              {" "}
              EE Component has been updated/replaced. Will it affect this
              module?
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        {/* <Dialog
          open={pendingDialogOpen}
          onClose={handlePendingDialogClose}
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
        >
          <DialogTitle align="left">Action required</DialogTitle>
          <IconButton
            onClick={handlePendingDialogClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                EE Component has been updated/replaced. Will it affect this
                module?
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handlePendingDialogClose}
                >
                  Yes
                </Button>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={pendingActionRequired}
                >
                  No
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog> */}

        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?swm_id=${selectedSWM?.swm_id}&ee_id=${selectedEEComponentDetails?.ee_id
              }&selectedZone=${selectedZone?.zone_position_name
              }&tabFromurl=${1}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              Software module dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>



        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            navigate(
              `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id
              }&zone=${selectedZone?.zone_position_name}&swm_id=${selectedSWM?.swm_id
              }&tab=${1}`,
            );
            setActionType2DialogOpen(false);
          }} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              Software module intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        {/* <Dialog
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
        >
          <DialogTitle align="left">Action required</DialogTitle>
          <IconButton
            onClick={handleActionType2DialogClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                Software module intersection linking is pending!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/type-approval?project_id=${selectedProjectDetails?.project_id}&variant_id=${selectedSingleVariantDetails?.variant_id}&ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}&swm_id=${selectedSWM?.swm_id}&linkModuleType=${linkModuleType?.SWM}`}
                >
                  <Button
                    sx={{ ...appTheme.commonBtnStyle }}
                    onClick={handleActionType2DialogClose}
                  >
                    Link Now
                  </Button>
                </Link>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleActionType2DialogClose}
                >
                  Link Later
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Details Dialog for SWM Component */}
        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="sm"
        // fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
            Software module Details
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: "60vh", // or "calc(100vh - 200px)" for more control
              overflowY: "auto",
              backgroundColor: "#fafafa",
              scrollbarColor: "#888 #f1f1f1",
              // padding: 3,
              wordBreak: "break-word",
            }}
          >
            {detailsItem ? (
              <>
                <DetailRow label="Name" value={detailsItem.name} />
                <DetailRow
                  label="Description"
                  value={detailsItem.description}
                />
                <DetailRow label="Summary" value={detailsItem.summary || "-"} />
                <DetailRow label="Version" value={detailsItem.version} />

                <DetailRow
                  label="Supplier Name"
                  value={detailsItem.supplier_name || "-"}
                />

                <DetailRow
                  label="Vehicle Functions"
                  value={(detailsItem.vehicle_functions || [])
                    ?.map((v) => v.name || v)
                    ?.join(", ")}
                />

                <DetailRow
                  label="Created At"
                  value={new Date(detailsItem.created_at).toLocaleString()}
                />
                <DetailRow
                  label="Updated At"
                  value={new Date(detailsItem.updated_at).toLocaleString()}
                />
              </>
            ) : (
              <Typography>No details available.</Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
            <Button
              onClick={() => setOpenDetailsDialog(false)}
              sx={{ ...appTheme.commonBtnStyle }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default React.memo(VlcSWModules);

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 26-07-2024
 * @description : Software module add dialog.
 * @param : openSWModuleAddDialog, setOpenSWModuleAddDialog, handleSWModuleAdd, vfOptionsList,
 * @return : The rendered software module add dialog.
 */
function VlcSWModuleAddDialog({
  supplierOptionsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedEEComponentDetails,
  openSWModuleAddDialog,
  setOpenSWModuleAddDialog,
  handleSWModuleAdd,
  vfOptionsList,
  selectedVariantsMany,
  variantOptionList,
  selectedSingleVariantDetails,
}) {
  const dispatch = useDispatch();
  /* Add SW Modules Fields */
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVfIds, setSelectedVFIds] = useState([]);
  const [selectedVariantsManySW, setSelectedVariantsManySW] = useState([]);
  const [supplierName, setSupplierName] = useState(null);
  const [githubLink, setGithubLink] = useState("");
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [swmAddTabValue, setSwmAddTabValue] = useState(0);

  const handleAttributeFormData = useCallback((data = {}) => {
    const nextData = {
      added: Array.isArray(data?.added) ? data.added : [],
      edited: Array.isArray(data?.edited) ? data.edited : [],
      deleted: Array.isArray(data?.deleted) ? data.deleted : [],
    };

    setAttributeFormData((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextData) ? prev : nextData,
    );
  }, []);

  const addedAttributeFormRows = useMemo(
    () =>
      (Array.isArray(attributeFormData?.added)
        ? attributeFormData.added
        : []
      ).map((attribute) => ({
        ...attribute,
        attribute_id: null,
        newlyAdded: true,
        edited: false,
      })),
    [attributeFormData?.added],
  );

  /* -------------- UseEffects -------------- */

  /* -------------- Add SW Modules -------------- */

  const clearAddSWModuleFields = () => {
    setName("");
    setNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSummaryError("");
    setSelectedVFIds([]);
    setSelectedVariantsManySW([]);
    setSupplierName(null);
    setGithubLink("");
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };

  const isAddSWModuleValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      name?.trim() !== "" &&
      name?.trim()?.length >= 3 &&
      !nameError &&
      // regexMin3?.test(name?.trim()) &&
      version?.trim() !== "" &&
      version?.trim()?.length >= 3 &&
      !versionError &&
      regexMin3?.test(version?.trim()) &&
      description?.trim() !== "" &&
      description?.trim()?.length >= 3 &&
      !descriptionError &&
      selectedVfIds?.length > 0 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3) &&
      (githubLink?.trim()?.length === 0 || githubLink?.trim()?.length >= 3)
    );
  };

  const isAddSWModuleDraftValid = () => {
    return (
      name?.trim() !== "" ||
      supplierName?.length > 0 ||
      version?.trim() !== "" ||
      description?.trim() !== "" ||
      summary?.trim() !== "" ||
      selectedVfIds?.length > 0 ||
      githubLink?.trim() !== ""
    );
  };

  const handleCloseSWModuleAdd = () => {
    setOpenSWModuleAddDialog(false);

    clearAddSWModuleFields();
  };


  const handleAddSWModule = () => {
    const result = handleSWModuleAdd({
      variant_ids: selectedEEComponentDetails?.variant_ids,
      version: version?.trim(),
      name: name?.trim(),
      summary,
      description,
      supplier_name: supplierName,
      vf_ids: selectedVfIds?.map(Number) || [],
      git_link: githubLink?.trim(),
      attributes: attributeFormData?.added || [],
    });
    handleCloseSWModuleAdd();

  };

  const handleAddSWModuleDraft = () => {
    const reqObj = {
      ...(supplierName ? { supplier_name: supplierName } : null),
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(version?.length > 0 ? { version: version?.trim() } : null),
      name: name?.trim(),
      ...(summary?.length > 0 ? { summary: summary } : null),
      ...(description?.length > 0 ? { description: description } : null),
      ...(selectedVfIds?.length > 0
        ? { vf_ids: selectedVfIds?.map(Number) || [] }
        : null),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      save_as_draft: true,
      current_time: moment().format(),
      ...(githubLink ? { git_link: githubLink } : null),
      attributes: attributeFormData?.added || [],
    };

    dispatch(
      HandleApiActions({
        api_name: "/VLC/swm/add",
        https_method: "POST",
        action_type: actionType.ADD_SW_MODULE_DRAFT,
        description: "Add in Save As Draft S/W Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() =>
      dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: selectedProjectDetails?.project_id,
            variant_id: selectedSingleVariantDetails?.variant_id,
            ee_id: selectedEEComponentDetails?.ee_id,
            // zone: selectedZone,
          },
          show_toast: false,
        }),
      ),
    );
    handleCloseSWModuleAdd();
  };

  useEffect(() => {
    customConsole({ selectedVfIds, vfOptionsList });
  }, [selectedVfIds, vfOptionsList]);

  useEffect(() => {
    const eeVariantIds = Array.isArray(selectedEEComponentDetails?.variant_ids)
      ? selectedEEComponentDetails.variant_ids
      : [];
    const optionIds = Array.isArray(variantOptionList)
      ? variantOptionList?.map((v) => v?.value)
      : [];
    const base = eeVariantIds.filter((id) => optionIds.includes(id));
    const currentId = selectedSingleVariantDetails?.variant_id;
    const merged = currentId != null ? [currentId, ...base] : base;
    const seen = new Set();
    const deduped = merged.filter((id) => {
      const key = String(id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    setSelectedVariantsManySW(deduped);
  }, [
    selectedEEComponentDetails,
    variantOptionList,
    selectedSingleVariantDetails,
  ]);

  // Ensure the current variant is selected when opening the dialog
  useEffect(() => {
    if (
      openSWModuleAddDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      setSelectedVariantsManySW((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr?.map(String).includes(String(id));
        if (hasId) return prevArr;
        return [id, ...prevArr];
      });
    }
    setSwmAddTabValue(0);
  }, [openSWModuleAddDialog, selectedSingleVariantDetails]);

  const isValidURL = (url) => {
    try {
      // If user types without https://, prepend it
      const formattedUrl = url.startsWith("http") ? url : `https://${url}`;
      new URL(formattedUrl);
      return true;
    } catch (err) {
      return false;
    }
  };

  return (
    <Box>
      <CustomDialogBoxV2
        open={openSWModuleAddDialog}
        onClose={handleCloseSWModuleAdd}
        title="Add Software Module"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box>
              {swmAddTabValue === 1 ? (
                <CustomButtonV2
                  type="cancel"
                  size="small"
                  btnName={"Back"}
                  handleOnClick={() => setSwmAddTabValue(0)}
                />
              ) : null}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {swmAddTabValue === 1 ? (
                <CustomButtonV2
                  type="other"
                  size="small"
                  btnName={"Save Draft"}
                  handleOnClick={handleAddSWModuleDraft}
                  disabled={!isAddSWModuleDraftValid()}
                />
              ) : null}

              <CustomButtonV2
                type="submit"
                size="small"
                btnName={swmAddTabValue == 0 ? "Next" : "Add"}
                handleOnClick={() => {
                  if (swmAddTabValue == 0) {
                    setSwmAddTabValue(1);
                    return;
                  }
                  handleAddSWModule();
                }}
                disabled={
                  swmAddTabValue === 0
                    ? !isAddSWModuleDraftValid()
                    : !isAddSWModuleValid()
                }
              />
            </Box>
          </Box>
        }

      >
        <CustomTabsV2
          defaultTab={swmAddTabValue}
          value={swmAddTabValue}
          onTabChange={(newValue) => setSwmAddTabValue(newValue)}
          tabs={[
            {
              name: "Details",
              content: (
                <Box sx={{ width: "450px", minHeight: "600px" }}>
                  <Stack spacing={2} mb={2}>
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Number"}
                      required={true}
                      type={"text"}
                      value={name}
                      setValue={(value) =>
                        value?.length <= 40 && setName(value)
                      }
                      maxLength={40}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Version"}
                      required={true}
                      type={"text"}
                      fldType={"textR"}
                      value={version}
                      setValue={(value) =>
                        value?.length <= 40 && setVersion(value)
                      }
                      maxLength={40}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Description"}
                      required={true}
                      value={description}
                      setValue={(value) =>
                        value?.length <= 500 && setDescription(value)
                      }
                      maxLength={500}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Summary"}
                      value={summary}
                      setValue={(value) =>
                        value?.length <= 250 && setSummary(value)
                      }
                      maxLength={250}
                    />

                    <CustomSelectV2
                      size={"small"}
                      required={true}
                      label={"Select Supplier Name"}
                      optionsList={supplierOptionsList}
                      selectedValue={supplierName || " "}
                      handleOnChange={(e) => setSupplierName(e.target.value)}
                    />

                    <MultiselectConnectorV2
                      size={"small"}
                      required
                      label={"Select Vehicle Functions"}
                      optionsList={selectedSingleVariantDetails?.vehicle_function_details?.map(
                        (el) => ({
                          value: el.id,
                          label: `${el.abbreviation} - ${el.name}`,
                        }),
                      )}
                      selectedValues={selectedVfIds || " "}
                      setSelectedValues={setSelectedVFIds}
                      placeholder={false}
                    />
                    <CustomFormTextFieldV2
                      size="small"
                      label="GitHub Link"
                      value={githubLink}
                      setValue={(value) =>
                        value?.length <= 100 && setGithubLink(value)
                      }
                      maxLength={100}
                    />

                    <MultiselectConnectorV2
                      disabled={true}
                      size={"small"}
                      multiple
                      selectedValues={selectedVariantsManySW || " "}
                      setSelectedValues={setSelectedVariantsManySW}
                      label={"Select Variants"}
                      optionsList={variantOptionList}
                      placeholder={false}
                      immutableValues={
                        selectedSingleVariantDetails?.variant_id != null
                          ? [selectedSingleVariantDetails?.variant_id]
                          : []
                      }
                    />
                  </Stack>
                </Box>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={"ADD"}
                    attributeFromApi={addedAttributeFormRows}
                    getFormData={handleAttributeFormData}
                  />
                </Stack>
              ),
            },
          ]}
        />
      </CustomDialogBoxV2>
    </Box>
  );
}

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 26-07-2024
 * @description : Software module update dialog.
 * @param : openSWModuleUpdateDialog, setOpenSWModuleUpdateDialog, handleSWModuleUpdate, selectedSWModuleUpdateDetails, setSelectedSWModuleUpdateDetails,
 * @return : The rendered software module update dialog.
 */
function VlcSWModuleUpdateDialog({
  variantOptionList,
  openSWModuleUpdateDialog,
  setOpenSWModuleUpdateDialog,
  handleSWModuleUpdate,
  selectedSWModuleUpdateDetails,
  setSelectedSWModuleUpdateDetails,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedVariantsMany,
  isSWMVersionImpactFlow,
  setIsSWMVersionImpactFlow,
  resumeSWMAttributeImpactFlowTrigger,
  resumeSWMVersionImpactFlowTrigger,
  onRedirectedActionDialogClose,
}) {
  const navigate = useNavigate();

  /* Update SW Modules Fields */
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [showAttributeConfirmation, setShowAttributeConfirmation] =
    useState(false);
  const [openUpdateFlowAttributeDialog, setOpenUpdateFlowAttributeDialog] =
    useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false); // State to track confirmation dialog
  const [
    isEditAttributeVersionDependencyFlow,
    setIsEditAttributeVersionDependencyFlow,
  ] = useState(false);
  const [
    isAttributeImpactUpdatePersisted,
    setIsAttributeImpactUpdatePersisted,
  ] = useState(false);
  const linkModuleType = { SWM: 1, EE: 2 };
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [selectedSWM, setSelectedSWM] = useState(null);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
  const [githubLink, setGithubLink] = useState("");
  const dispatch = useDispatch();

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  //Rule set
  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetList, setRuleSetList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  // Rule set question flow
  const [ruleSetTree, setRuleSetTree] = useState(null);
  // path: [{ node: {type,data}, answer: 'yes'|'no'|null }]
  const [rulePath, setRulePath] = useState([]);
  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [executionId, setExecutionId] = useState(null);

  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);
  const [disableActionButton, setDisableActionButton] = useState(false);
  const lastResumeSWMAttributeImpactFlowTriggerRef = useRef(0);
  const lastResumeSWMVersionImpactFlowTriggerRef = useRef(0);

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath?.length) return;

    // copy up to index (inclusive) and set the chosen answer
    const newPath = rulePath
      ?.slice(0, idx + 1)
      ?.map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      // push the next node and reset its answer
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else {
      // If we have a category, this is a terminal node
      if (currentEntry?.node?.data?.category) {
        // Check if homologation is required for this category
        if (currentEntry?.node?.data?.requires_homologation) {
          // If homologation is required, call updateChangesAndClose
          updateChangesAndClose();
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        } else {
          // If homologation is not required, just close the dialog
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        }
      } else {
        // Otherwise just update the current answer and keep dialog open
        setRulePath(newPath);
      }
    }
  };
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);

  // useEffect(() => {
  //   if (openSWModuleUpdateDialog == true && selectedSWModuleUpdateDetails) {
  //     setVersion(selectedSWModuleUpdateDetails?.version || "");
  //     setSelectedVariantsMany(
  //       variantOptionList
  //         ?.filter(
  //           (variant) =>
  //             variant?.value !== selectedSingleVariantDetails?.variant_id &&
  //             selectedSWModuleUpdateDetails?.variant_ids
  //               ?.map((variant) => variant)
  //               ?.includes(variant?.value)
  //         )
  //         ?.map((data) => parseInt(data?.value)) || []
  //     );
  //   }
  // }, [openSWModuleUpdateDialog, selectedSWModuleUpdateDetails]);

  /* -------------- Update SW Module -------------- */

  const clearUpdateSWModuleFields = () => {
    setVersion("");
    setVersionError("");
    // setDiscp("");
    setDiscpError("");
    setGithubLink("");
  };

  const isUpdateSWModuleValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      !versionError &&
      version?.trim() !== selectedSWModuleUpdateDetails?.version &&
      version?.trim()?.length >= 3 &&
      regexMin3?.test(version?.trim()) &&
      discp?.trim()?.length >= 3
    );
  };

  const handleCloseSWModuleUpdate = () => {
    setOpenSWModuleUpdateDialog(false);
    onRedirectedActionDialogClose?.();

    clearUpdateSWModuleFields();

    setSelectedSWModuleUpdateDetails(null);
    setShowAttributeConfirmation(false);
    setOpenUpdateFlowAttributeDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
    setIsSWMVersionImpactFlow(false);
    setIsEditAttributeVersionDependencyFlow(false);
    setIsAttributeImpactUpdatePersisted(false);
    setDisableActionButton(false);
  };

  useEffect(() => {
    if (
      resumeSWMAttributeImpactFlowTrigger &&
      resumeSWMAttributeImpactFlowTrigger !==
      lastResumeSWMAttributeImpactFlowTriggerRef.current &&
      selectedSWModuleUpdateDetails
    ) {
      lastResumeSWMAttributeImpactFlowTriggerRef.current =
        resumeSWMAttributeImpactFlowTrigger;
      setShowAttributeConfirmation(false);
      setShowConfirmation(false);
      setShowIntersectionImpactPrompt(false);
      setIsSWMVersionImpactFlow(false);
      setIsEditAttributeVersionDependencyFlow(false);
      setIsAttributeImpactUpdatePersisted(true);
      setTimeout(() => {
        setOpenUpdateFlowAttributeDialog(true);
      }, 200);
    }
  }, [resumeSWMAttributeImpactFlowTrigger, selectedSWModuleUpdateDetails]);

  useEffect(() => {
    if (
      resumeSWMVersionImpactFlowTrigger &&
      resumeSWMVersionImpactFlowTrigger !==
      lastResumeSWMVersionImpactFlowTriggerRef.current &&
      selectedSWModuleUpdateDetails
    ) {
      lastResumeSWMVersionImpactFlowTriggerRef.current =
        resumeSWMVersionImpactFlowTrigger;
      setShowAttributeConfirmation(false);
      setOpenUpdateFlowAttributeDialog(false);
      setShowConfirmation(false);
      setShowIntersectionImpactPrompt(false);
      setIsAttributeImpactUpdatePersisted(false);
      setIsEditAttributeVersionDependencyFlow(false);
      setIsSWMVersionImpactFlow(true);
    }
  }, [resumeSWMVersionImpactFlowTrigger, selectedSWModuleUpdateDetails]);

  const handleclearSWModuleUpdate = () => {
    clearUpdateSWModuleFields();
    setSelectedSWModuleUpdateDetails(null);
  };

  const handleUpdateClick = () => {
    if (isSWMVersionImpactFlow) {
      updateVersionImpactSWModule();
      return;
    }

    setShowAttributeConfirmation(true);
  };

  const updateVersionImpactSWModule = async () => {
    setDisableActionButton(true);

    try {
      const reqObj = {
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleUpdateDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
        swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
        version: version?.trim(),
        ...(githubLink ? { git_link: githubLink } : null),
        will_it_impact: true,
        variant_ids: selectedEEComponentDetails?.variant_ids,
        reason: discp,
      };

      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_sw_module,
          params: reqObj,
          show_toast: true,
        }),
      );

      if (response?.error) {
        setDisableActionButton(false);
        return;
      }

      await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: {
            current_time: new Date().toISOString(),
            project_id: Number(selectedSWModuleUpdateDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
            swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
            is_dependency_linking: false,
            dependency: true,
          },
          show_toast: false,
        }),
      );

      setIsSWMVersionImpactFlow(false);
      setIsEditAttributeVersionDependencyFlow(false);
      handleCloseSWModuleUpdate();
      setSelectedSWM(selectedSWModuleUpdateDetails);
      setActionType1DialogOpen(true);
      getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update action type", error);
    }

    setDisableActionButton(false);
  };

  const handleEditAttributeVersionDependencyYes = async () => {
    setIsEditAttributeVersionDependencyFlow(false);
    await confirmUpdateAndRedirect({
      selectedSWModuleUpdateDetails,
    });
  };

  const handleEditAttributeVersionIntersectionNo = async () => {
    setShowIntersectionImpactPrompt(false);
    setIsEditAttributeVersionDependencyFlow(false);
    await updateChangesWithoutStandardAndClose();
    handleCloseSWModuleUpdate();
  };

  const handleEditAttributeVersionIntersectionYes = async () => {
    setIsEditAttributeVersionDependencyFlow(false);
    await confirmUpdateAndRedirect({
      selectedSWModuleUpdateDetails,
      willImptIntrOnly: true,
    });
  };

  const handlePersistedAttributeDependencyYes = async () => {
    setDisableActionButton(true);
    setShowConfirmation(false);

    try {
      const updateSWModulePayload = {
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleUpdateDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
        swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
        version: version?.trim(),
        ...(githubLink ? { git_link: githubLink } : null),
        will_it_impact: true,
        variant_ids: selectedEEComponentDetails?.variant_ids,
        reason: discp,
      };

      const updateResponse = await dispatch(
        HandleApiActions({
          ...labCar.update_sw_module,
          params: updateSWModulePayload,
          show_toast: true,
        }),
      );

      if (updateResponse?.error) {
        setDisableActionButton(false);
        return;
      }

      await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: {
            current_time: new Date().toISOString(),
            project_id: Number(selectedSWModuleUpdateDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
            swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
            is_dependency_linking: false,
            dependency: true,
          },
          show_toast: false,
        }),
      );

      setIsAttributeImpactUpdatePersisted(false);
      handleCloseSWModuleUpdate();
      getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update action type", error);
      setDisableActionButton(false);
    }
  };

  const handlePersistedAttributeIntersectionNo = () => {
    setShowIntersectionImpactPrompt(false);
    setIsAttributeImpactUpdatePersisted(false);
    handleCloseSWModuleUpdate();
  };

  const handlePersistedAttributeIntersectionYes = async () => {
    setDisableActionButton(true);
    setShowIntersectionImpactPrompt(false);

    try {
      await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: {
            current_time: new Date().toISOString(),
            project_id: Number(selectedSWModuleUpdateDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
            swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
            is_dependency_linking: false,
            intersection: true,
          },
          show_toast: false,
        }),
      );

      setIsAttributeImpactUpdatePersisted(false);
      handleCloseSWModuleUpdate();
      getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update action type", error);
      setDisableActionButton(false);
    }
  };

  const handleUpdateSWModule = ({ }) => {
    if (
      selectedSWModuleUpdateDetails &&
      selectedSWModuleUpdateDetails?.swm_id
    ) {
      handleSWModuleUpdate({
        swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
        version: version?.trim(),
      });

      handleCloseSWModuleUpdate();
    }
  };

  const confirmUpdateAndRedirect = async ({
    selectedSWModuleUpdateDetails,
    willImptIntrOnly = false,
  }) => {
    setDisableActionButton(true);
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version: version?.trim(),
      ...(githubLink ? { git_link: githubLink } : null),
      will_it_impact: true,
      variant_ids: selectedEEComponentDetails?.variant_ids,
      reason: discp,
    };
    try {
      // Dispatch the update action type API action
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_sw_module,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!response.error) {
        handleCloseSWModuleUpdate();
        willImptIntrOnly
          ? handleActionType2DialogOpen()
          : handleActionType1DialogOpen();
        if (willImptIntrOnly) {
          const resp = await dispatch(
            HandleApiActions({
              ...labCar.update_action_type,
              params: {
                current_time: new Date().toISOString(),
                project_id: Number(selectedSWModuleUpdateDetails?.project_id),
                variant_id: Number(selectedSingleVariantDetails?.variant_id),
                ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
                swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
                is_dependency_linking: true,
              },
              show_toast: false,
            }),
          );
        }
        await getSWModulesList({ force: true });
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
    setDisableActionButton(false);
  };

  const handleCloseSWModuleUpdateDialog = () => {
    setOpenSWModuleUpdateDialog(false);
  };

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      tickets_type: 1,
    };

    try {
      // First API call
      const response = await dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets,
          params: reqObj,
          show_toast: false,
        }),
      );
      setShowIntersectionImpactPrompt(false);
      setShowConfirmation(false); // Reset confirmation on close
      setOpenSWModuleUpdateDialog(false);

      // Extract and deduplicate standards
      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              ?.map((ticket) => ticket?.ls_details?.standard_number)
              ?.filter((std) => Boolean(std)) // remove empty/undefined
            : [],
        ),
      ];

      if (standardsWithRuleSet?.length > 0) {
        // Second API call with standards
        const standardWithRuleSet = await dispatch(
          HandleApiActions({
            ...ruleEngineActions?.get_rule_engine_with_standards,
            params: { standard: standardsWithRuleSet },
            show_toast: false,
          }),
        );

        if (
          standardWithRuleSet?.data &&
          (Array.isArray(standardWithRuleSet.data)
            ? standardWithRuleSet.data?.length > 0
            : Object.keys(standardWithRuleSet?.data)?.length > 0)
        ) {
          // Handle the case where there are standards with rulesets
          handleCloseSWModuleUpdateDialog();
          setShowRuleSetListDialog(true);
          // store the full rule objects so we can send the id later
          setRuleSetList(standardWithRuleSet?.data);
        } else {
          handleCloseSWModuleUpdate();
          updateChangesWithoutStandardAndClose();
        }
        return standardWithRuleSet.data || [];
      } else {
        updateChangesAndClose();
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch standard with ruleset", error);
      return false;
    }
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseSWModuleUpdate();
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_rule_set_tree,
          params: {
            rule_set_id: ruleSetIdToUse,
          },
          show_toast: false,
        }),
      );

      if (response?.data) {
        // Store the full tree and open the question dialog
        setRuleSetTree(response.data);
        // Normalize root node into { type, data }
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setRulePath([{ node: root, answer: null }]);
          setShowRuleSetQuestionDialog(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch rule set questions", error);
    }
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version: version?.trim(),
      will_it_impact: false,
      requires_homologation: false,
      reason: discp,
    };

    try {
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_sw_module,
          params: reqObj,
          show_toast: true,
        }),
      );



      if (response?.data?.snow_resp) {
        // small delay ensures backdrop cleanup
        setTimeout(() => {
          setTicketsDialogOpen(true);
        }, 200);
      }

      await getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const updateChangesAndClose = async () => {
    // Get requires_homologation value from the last node in the rule path
    const lastNode = rulePath[rulePath?.length - 1]?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;

    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version: version?.trim(),
      will_it_impact: false,
      requires_homologation: requiresHomologation,
      reason: discp,
    };
    try {
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_sw_module,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!response.error) {
        handleCloseSWModuleUpdate();
      }
      if (response?.data?.snow_resp) {
        setTimeout(() => {
          setTicketsDialogOpen(true);
        }, 200);
      }
      await getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleActionType1DialogOpen = () => {
    setSelectedSWM(selectedSWModuleUpdateDetails);
    setActionType1DialogOpen(true);
  };
  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogOpen = () => {
    setSelectedSWM(selectedSWModuleUpdateDetails);
    setActionType2DialogOpen(true);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  useEffect(() => {
    if (openSWModuleUpdateDialog) {
      setDiscp("");
    }
  }, [openSWModuleUpdateDialog]);

  useEffect(() => {
    if (openSWModuleUpdateDialog && selectedSWModuleUpdateDetails) {
      setVersion(selectedSWModuleUpdateDetails?.version || "");
      // setDiscp("");
      setDiscpError("");
    } else {
      // setDiscp("");
      setDiscpError("");
    }
  }, [openSWModuleUpdateDialog, selectedSWModuleUpdateDetails]);

  const isDecisionMatrixContextValid = (data) => {
    if (!data?.standards || data.standards.length === 0) {
      return false;
    }

    return data.standards.some((standard) => {
      if (!standard.rule_sets || standard.rule_sets.length === 0) {
        return false;
      }

      return standard.rule_sets.some(
        (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
      );
    });
  };
  const loadDecisionMatrixContext = async () => {
    // 1. Fetch context based on component
    const standardsRes = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.decision_matrix_get_context,
        params: {
          component_type: "SWM",
          component_id: selectedSWModuleUpdateDetails.swm_id,
          project_id: selectedProjectDetails.project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
        },
        show_toast: false,
      }),
    );
    setOpenSWModuleUpdateDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;


    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);

    setShowDecisionMatrix(true);
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_start,
          params: {
            workflowId: payload.workflowId,
            version: payload.version,
          },
          show_toast: false,
        }),
      );

      setExecutionId(res.executionId);
      return res;
    }

    if (mode === "COMPLETE") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_node_complete,
          params: {
            executionId: payload.executionId,
            nodeId: payload.nodeId,
            input: payload.input,
          },
          show_toast: false,
        }),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = (executionId) => {
    // navigate / open drawer / open audit view
  };
  const updateComponentAndCheckForDecisionMatrix = async ({
    standardCode,
  } = {}) => {
    try {


      const reCreateTicketParams = {
        details: createTicketData,
        reason: discp,
      };

      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.re_create_ticket_v1,
          params: reCreateTicketParams,
          show_toast: true,
        }),
      );

      if (!res?.error && standardCode) {
        handleTicketCreationSuccess(standardCode);
      }

      return res;

      // Once updated, check if decision matrix needs to be shown.
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleTicketCreationSuccess = (standardCode) => {
    setDisabledStandards((prev) => [...new Set([...prev, standardCode])]);
  };

  //function to handel a get a list of tickets for a legal requirements
  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "swm",
        entity_id: Number(selectedSWModuleUpdateDetails?.swm_id),
        ...(standard_name != null ? { standard_name } : {}),
      };

      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_similar_tickets,
          params: reCreateTicketParams,
          show_toast: true,
        }),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handelGetAiSuggestion = (ticket) => {
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "SW Module",
    });
    setOpenAiPanel(true);
    setSelectedTicket(ticket);
  };

  const handelCloseDecisionMatrix = () => {
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    setRulePath([]);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
  };
  return (
    <Box>


      <CustomDialogBoxV2
        open={openSWModuleUpdateDialog}
        onClose={handleCloseSWModuleUpdate}
        title={"Update Software Module"}
        dialogActions={
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <CustomButtonV2
              btnName={"Cancel"}
              size="small"
              type="cancel"
              handleOnClick={handleCloseSWModuleUpdate}
            />
            <CustomButtonV2
              btnName={"Update"}
              size="small"
              type="submit"
              handleOnClick={handleUpdateClick}
              disabled={!isUpdateSWModuleValid()}
            />
          </Box>
        }
      >
        <Stack spacing={2} mb={2} sx={{ width: "350px", height: "150px" }}>
          <CustomFormTextFieldV2
            size={"small"}
            label={"Version"}
            required={true}
            type={"text"}
            fldType="textR"
            value={version}
            setValue={(value) => value?.length <= 40 && setVersion(value)}
            maxLength={40}
          />

          <CustomFormTextFieldV2
            size={"small"}
            label={"Reason"}
            required={true}
            value={discp}
            setValue={(value) => value?.length <= 100 && setDiscp(value)}
            maxLength={100}
          />
          <CustomDialogBoxV2
            open={showAttributeConfirmation}
            onClose={() => {
              setShowAttributeConfirmation(false);
            }}
            title="Action"
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  width: "350px",
                  justifyContent: "space-between",
                  height: "50px",
                }}
              >
                <CustomButtonV2
                  size="small"
                  type="cancel"
                  btnName="No"
                  handleOnClick={() => {
                    setShowAttributeConfirmation(false);

                    setTimeout(() => {
                      setShowConfirmation(true);
                    }, 200);
                  }}
                />
                <CustomButtonV2
                  size="small"
                  type="submit"
                  btnName="Yes"
                  handleOnClick={async () => {
                    setDisableActionButton(true);
                    setShowAttributeConfirmation(false);

                    try {
                      const reqObj = {
                        current_time: new Date().toISOString(),
                        project_id: Number(
                          selectedSWModuleUpdateDetails?.project_id,
                        ),
                        variant_id: Number(
                          selectedSingleVariantDetails?.variant_id,
                        ),
                        ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
                        swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
                        version: version?.trim(),
                        ...(githubLink ? { git_link: githubLink } : null),
                        will_it_impact: true,
                        variant_ids: selectedEEComponentDetails?.variant_ids,
                        reason: discp,
                      };

                      const response = await dispatch(
                        HandleApiActions({
                          ...labCar.update_sw_module,
                          params: reqObj,
                          show_toast: true,
                        }),
                      );

                      if (response?.error) {
                        setDisableActionButton(false);
                        return;
                      }
                      setOpenSWModuleUpdateDialog(false);
                      setIsAttributeImpactUpdatePersisted(true);

                      await dispatch(
                        HandleApiActions({
                          ...labCar.update_action_type,
                          params: {
                            current_time: new Date().toISOString(),
                            project_id: Number(
                              selectedSWModuleUpdateDetails?.project_id,
                            ),
                            variant_id: Number(
                              selectedSingleVariantDetails?.variant_id,
                            ),
                            ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
                            swm_id: Number(
                              selectedSWModuleUpdateDetails?.swm_id,
                            ),
                            is_dependency_linking: false,
                            attribute: true,
                          },
                          show_toast: false,
                        }),
                      );

                      setTimeout(() => {
                        setOpenUpdateFlowAttributeDialog(true);
                      }, 200);
                      getSWModulesList({ force: true });
                    } catch (error) {
                      console.error("Failed to update action type", error);
                    }

                    setDisableActionButton(false);
                  }}
                  disabled={disableActionButton}
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                }}
              >
                Will it impact the existing attributes?
              </Typography>
            </Box>
          </CustomDialogBoxV2>

        </Stack>
      </CustomDialogBoxV2>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openUpdateFlowAttributeDialog}
        setOpenAttributeUpdateDialog={setOpenUpdateFlowAttributeDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenUpdateFlowAttributeDialog(false);
          onRedirectedActionDialogClose?.();
        }}
        onClickSuccess={() => {
          getSWModulesList({ force: true });
          setOpenUpdateFlowAttributeDialog(false);
        }}
        selectedComponent={selectedSWModuleUpdateDetails}
        componentType={"swm"}
        isAttributeImpactFlow
        variantId={selectedSingleVariantDetails?.variant_id}
        onIntersectionNo={loadDecisionMatrixContext}
        attributeImpactActionType={{ dependency: true }}
        reason={discp}
        setReason={setDiscp}
        reasonError={discpError}
        onAttributeImpactComplete={() => {
          getSWModulesList({ force: true });
          setOpenUpdateFlowAttributeDialog(false);
          setSelectedSWM(selectedSWModuleUpdateDetails);
          setActionType1DialogOpen(true);
        }}
      />

      <CustomDialogBoxV2
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
        }}
        title={"Action"}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <CustomButtonV2
              btnName={"No"}
              size="small"
              type="cancel"
              handleOnClick={() => {
                setShowConfirmation(false);

                setTimeout(() => {
                  setShowIntersectionImpactPrompt(true);
                }, 200);
              }}
            />
            <CustomButtonV2
              btnName={"Yes"}
              size="small"
              type="submit"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  handlePersistedAttributeDependencyYes();
                  return;
                }

                if (isEditAttributeVersionDependencyFlow) {
                  handleEditAttributeVersionDependencyYes();
                  return;
                }

                confirmUpdateAndRedirect({
                  selectedSWModuleUpdateDetails,
                });
              }}
              disabled={disableActionButton}
            />
          </Box>
        }
      >
        <Box
          sx={{
            width: "350px",
            height: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              // ...componentStyle.font.text,
              fontSize: "15px",
            }}
          >
            Will it impact the existing dependencies?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={showIntersectionImpactPrompt}
        onClose={() => {
          setShowIntersectionImpactPrompt(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  handlePersistedAttributeIntersectionNo();
                  return;
                }

                if (isEditAttributeVersionDependencyFlow) {
                  handleEditAttributeVersionIntersectionNo();
                  return;
                }

                loadDecisionMatrixContext();
                updateChangesWithoutStandardAndClose();
              }}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  handlePersistedAttributeIntersectionYes();
                  return;
                }

                if (isEditAttributeVersionDependencyFlow) {
                  handleEditAttributeVersionIntersectionYes();
                  return;
                }

                confirmUpdateAndRedirect({
                  selectedSWModuleUpdateDetails,
                  willImptIntrOnly: true,
                });
              }}
            />
          </Box>
        }
      >
        <Box
          sx={{
            width: "350px",
            height: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              ...componentStyle.font.text,
            }}
          >
            Will it impact the existing intersection?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <Box>
        {/* Action Pending Dialog */}

        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?swm_id=${selectedSWM?.swm_id}&ee_id=${selectedEEComponentDetails?.ee_id
              }&selectedZone=${selectedZone?.zone_position_name
              }&tabFromurl=${1}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              Software module dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        {/* <Dialog
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
        >
          <DialogTitle align="left">Action required</DialogTitle>
          <IconButton
            onClick={handleActionType1DialogClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                Software module dependency linking is pending!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  style={{ textDecoration: "none" }}
                  to={`/dashboard/labcar?swm_id=${selectedSWM?.swm_id}&ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}&linkModuleType=${linkModuleType?.SWM}`}
                >
                  <CustomButtonV2
                    type="submit"
                    btnName="Link Now"
                    handleOnClick={() => setIsLinkSoftwareModulesScreen(true)}
                  />
                </Link>

                <CustomButtonV2
                  type="submit"
                  btnName="Link Later"
                  handleOnClick={handleActionType1DialogClose}
                />
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog> */}

        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            navigate(
              `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id
              }&zone=${selectedZone?.zone_position_name}&swm_id=${selectedSWM?.swm_id
              }&tab=${1}`,
            );
            setActionType2DialogOpen(false);
          }} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              Software module intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        {/* <Dialog
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
        >
          <DialogTitle align="left">Action required</DialogTitle>
          <IconButton
            onClick={handleActionType2DialogClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                Software module intersection linking is pending!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/type-approval?project_id=${selectedProjectDetails?.project_id}&variant_id=${selectedSingleVariantDetails?.variant_id}&ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}&swm_id=${selectedSWM?.swm_id}&linkModuleType=${linkModuleType?.SWM}`}
                >
                  <Button
                    sx={{ ...appTheme.commonBtnStyle }}
                    onClick={handleActionType2DialogClose}
                  >
                    Link Now
                  </Button>
                </Link>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleActionType2DialogClose}
                >
                  Link Later
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog> */}

        {/* Handle Ticket Page Dialog */}
        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets?tab=${1}`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                // ...componentStyle.font.text,
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>

        {/* <Dialog
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
        >
          <DialogTitle align="left">Tickets Recreated</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                Successfully created the tickets for the intersections!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/type-approval?project_id=${
                    selectedProjectDetails?.project_id
                  }&setTabValue=${2}&varint_id=${
                    selectedSingleVariantDetails?.variant_id
                  }`}
                >
                  <Button sx={{ ...appTheme.commonBtnStyle }}>View</Button>
                </Link>

                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleTicketsDialogClose}
                >
                  Close
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog> */}

        {/* Rule set list dialog */}
        <CustomDialogBoxV2
          open={showRuleSetListDialog}
          onClose={handleRuleSetListDialogClose}
          title="Select Rule Set"
          onClickSuccess={async () => {
            // fetch and open the question flow for the selected ruleset
            await viewRuleSetQuestions(selectedRuleSet);
            // keep the list dialog closed
            setShowRuleSetListDialog(false);
          }}
          onDisabled={!selectedRuleSet}
          onClickReject={handleRuleSetListDialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Confirm"}
          rejectButtonName={"Cancel"}
        >
          <Stack spacing={2} sx={{ width: "350px", height: "50px" }}>
            <CustomSelectV2
              size={"small"}
              label={"Select Rule Set"}
              required={true}
              // variant={"standard"}
              optionsList={
                ruleSetList?.map((rs) => ({
                  value: rs?.rule_set_id || rs?.id,
                  label: rs?.name,
                })) || []
              }
              selectedValue={selectedRuleSet || " "}
              handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
            // sltFldStyle={{ width: "100%", marginTop: 6 }}
            />
          </Stack>
        </CustomDialogBoxV2>



        {/* Rule set question dialog - progressive yes/no navigation */}
        <CustomDialogBoxV2
          open={showRuleSetQuestionDialog}
          onClose={() => {
            setShowRuleSetQuestionDialog(false);
            setRuleSetTree(null);
            setRulePath([]);
            setSelectedRuleSet("");
          }}
          title={`Rule Set: ${ruleSetTree?.name || ""}`}
          contentSx={{ minHeight: "350px", width: "450px" }}

        >
          <DialogContent>
            <Stack spacing={2} sx={{}}>
              {/* Full path view - show all questions answered so far and allow editing */}
              <Stack spacing={1}>
                {rulePath?.map((entry, idx) => {
                  const isCategory = entry.node?.type === "category";
                  return (
                    <Box
                      key={idx}
                      sx={{
                        border: "1px solid #e0e0e0",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          idx === rulePath?.length - 1 ? "#fafafa" : "white",
                      }}
                    >
                      {!isCategory ? (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.node?.data?.text}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Box>
                              <CustomButtonV2

                                btnName={"Yes"}
                                handleOnClick={() => handleAnswerAt(idx, "yes")}
                                {...(entry.answer === "yes"
                                  ? { type: "submit" }
                                  : { type: "cancel" })}


                              />
                            </Box>
                            <Box>
                              <CustomButtonV2

                                btnName={"No"}
                                handleOnClick={() => handleAnswerAt(idx, "no")}
                                {...(entry.answer === "no"
                                  ? { type: "submit" }
                                  : { type: "cancel" })}


                              />
                            </Box>
                          </Box>
                        </Stack>
                      ) : (
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session Ended: {entry.node?.data?.description}
                          </Typography>

                        </Stack>
                      )}
                    </Box>
                  );
                })}

                {/* If last node is category show Finish, otherwise allow answering last question if unanswered */}
                {rulePath?.length > 0 &&
                  rulePath[rulePath?.length - 1]?.node?.type === "category" ? (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    mt={2}
                  >
                    <CustomButtonV2
                      type="submit"
                      btnName={"Finish"}
                      // sx={{ ...appTheme.commonBtnStyle }}
                      handleOnClick={() => {
                        // Check if the last node requires homologation
                        const lastNode = rulePath[rulePath?.length - 1]?.node;

                        if (lastNode?.data?.requires_homologation) {
                          // If homologation is required, just call the updateChangesAndClose function and close the dialog
                          updateChangesAndClose();
                          setShowRuleSetQuestionDialog(false);
                          setRuleSetTree(null);
                          setRulePath([]);
                          setSelectedRuleSet("");
                          handleCloseSWModuleUpdate();
                        } else {
                          // If homologation is not required, just close the dialog
                          updateChangesAndClose();
                          setShowRuleSetQuestionDialog(false);
                          setRuleSetTree(null);
                          setRulePath([]);
                          setSelectedRuleSet("");
                          handleCloseSWModuleUpdate();
                        }
                      }}
                    />
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </DialogContent>
        </CustomDialogBoxV2>

        <Dialog
          open={showDecisionMatrix}
          onClose={handelCloseDecisionMatrix}
          fullScreen
          PaperProps={{
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          }}
        >
          {/* Deloitte AppBar */}
          <AppBar
            position="sticky"
            sx={{
              backgroundColor: "#26890D",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Decision Matrix
              </Typography>
              <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Body */}
          <Box
            sx={{
              flex: 1,
              height: "calc(100vh - 64px)",
              overflow: "auto",
              p: 2,
            }}
          >
            {showDecisionMatrix && (
              <DecisionMatrixPanel
                context={decisionMatrixContext}
                key={showDecisionMatrix ? "open" : "closed"}
                onExecuteWorkflow={handleExecuteWorkflow}
                onViewExecution={handleViewExecution}
                onClose={handelCloseDecisionMatrix}
                onDecisionMatrixAction={
                  updateComponentAndCheckForDecisionMatrix
                }
                ///////////////////////
                handelGetSimilerTickets={handelGetSimilerTickets}
                similarTickets={similarTickets}
                setSimilarTickets={setSimilarTickets}
                entityDetails={selectedEntity}
                handelGetAiSuggestion={handelGetAiSuggestion}
                selectedTicket={selectedTicket}
                setSelectedTicket={setSelectedTicket}
                openAiPanel={openAiPanel}
                setOpenAiPanel={setOpenAiPanel}
                setCreateTicketData={setCreateTicketData}
                disabledStandards={disabledStandards}
              />
            )}
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
}

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 26-07-2024
 * @description : Software module replace dialog.
 * @param : openSWModuleReplaceDialog, setOpenSWModuleReplaceDialog, handleSWModuleReplace, selectedSWModuleReplaceDetails, setSelectedSWModuleReplaceDetails, vfOptionsList,
 * @return : The rendered software module replace dialog.
 */
function VlcSWModuleReplaceDialog({
  openSWModuleReplaceDialog,
  setOpenSWModuleReplaceDialog,
  handleSWModuleReplace,
  selectedSWModuleReplaceDetails,
  setSelectedSWModuleReplaceDetails,
  vfOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  title,
  supplierOptionsList,
}) {
  /* Replace SW Modules Fields */
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [version, setVersion] = useState("");
  const [supplierName, setSupplierName] = useState(null);
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVfIds, setSelectedVfIds] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false); // State to track confirmation dialog
  const linkModuleType = { SWM: 1, EE: 2 };
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [selectedSWM, setSelectedSWM] = useState(null);

  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);
  const [githubLink, setGithubLink] = useState("");
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });

  const handleAttributeFormData = useCallback((data = {}) => {
    const nextData = {
      added: Array.isArray(data?.added) ? data.added : [],
      edited: Array.isArray(data?.edited) ? data.edited : [],
      deleted: Array.isArray(data?.deleted) ? data.deleted : [],
    };

    setAttributeFormData((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextData) ? prev : nextData,
    );
  }, []);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  //Rule set
  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetList, setRuleSetList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  // Rule set question flow
  const [ruleSetTree, setRuleSetTree] = useState(null);
  // path: [{ node: {type,data}, answer: 'yes'|'no'|null }]
  const [rulePath, setRulePath] = useState([]);
  const [selectedVariantsManySW, setSelectedVariantsManySW] = useState([]);
  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [executionId, setExecutionId] = useState(null);

  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);
  const [disableActionButton, setDisableActionButton] = useState(false);
  const [swmTabValue, setSwmTabValue] = useState(0);
  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath?.length) return;

    // copy up to index (inclusive) and set the chosen answer
    const newPath = rulePath
      ?.slice(0, idx + 1)
      ?.map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      // push the next node and reset its answer
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else {
      // If we have a category, this is a terminal node
      if (currentEntry?.node?.data?.category) {
        // Check if homologation is required for this category
        if (currentEntry?.node?.data?.requires_homologation) {
          // If homologation is required, call replaceChangesAndClose
          replaceChangesAndClose();
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        } else {
          // If homologation is not required, just close the dialog
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        }
      } else {
        // Otherwise just update the current answer and keep dialog open
        setRulePath(newPath);
      }
    }
  };
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);

  const dispatch = useDispatch();

  /* -------------- UseEffects -------------- */

  useEffect(() => {
    if (openSWModuleReplaceDialog) {
      setDiscp("");
    }
  }, [openSWModuleReplaceDialog]);

  useEffect(() => {
    if (openSWModuleReplaceDialog == true && selectedSWModuleReplaceDetails) {
      setName(selectedSWModuleReplaceDetails?.name || "");
      setVersion(selectedSWModuleReplaceDetails?.version || "");
      setDescription(selectedSWModuleReplaceDetails?.description || "");
      setSupplierName(selectedSWModuleReplaceDetails?.supplier_name || null);
      setSelectedVariantsMany(
        variantOptionList
          ?.filter(
            (variant) =>
              variant?.value !== selectedSingleVariantDetails?.variant_id &&
              selectedSWModuleReplaceDetails?.variant_ids
                ?.map((variant) => variant)
                ?.includes(variant?.value),
          )
          ?.map((data) => parseInt(data?.value)) || [],
      );
      setSummary(selectedSWModuleReplaceDetails?.summary || "");
      setSelectedVfIds(
        (selectedSWModuleReplaceDetails?.vf_ids || [])
          ?.map((vfId) => Number(vfId))
          ?.filter((vfId) => !isNaN(vfId)),
      );
      // setDiscp(selectedSWModuleReplaceDetails?.reason || "");
    }
    setSwmTabValue(0);
  }, [openSWModuleReplaceDialog, selectedSWModuleReplaceDetails]);

  /* -------------- Replace SW Module -------------- */

  const clearReplaceSWModuleFields = () => {
    setName("");
    setNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setSelectedVariantsMany([]);
    setDescriptionError("");
    setSummary("");
    setSummaryError("");
    setSelectedVfIds([]);
    // setDiscp("");
    setDiscpError("");
    setSelectedVariantsManySW([]);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };

  const isReplaceSWModuleDetailsValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      !nameError &&
      !versionError &&
      !descriptionError &&
      selectedVfIds?.length > 0 &&
      // name?.trim()?.length >= 3 &&
      // version?.trim()?.length >= 3 &&
      regexMin3?.test(version?.trim()) &&
      // regexMin3?.test(name?.trim()) &&
      name?.trim() !== selectedSWModuleReplaceDetails?.name &&
      (name?.trim() !== selectedSWModuleReplaceDetails?.name ||
        version?.trim() !== selectedSWModuleReplaceDetails?.version ||
        description?.trim() !== selectedSWModuleReplaceDetails?.description ||
        summary?.trim() !== selectedSWModuleReplaceDetails?.summary ||
        selectedVfIds?.length !==
        selectedSWModuleReplaceDetails?.vf_ids?.length) &&
      name?.trim()?.length >= 3 &&
      version?.trim()?.length >= 3 &&
      description?.trim()?.length >= 3 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3) &&
      (githubLink?.trim()?.length === 0 || githubLink?.trim()?.length >= 3)
    );
  };

  const isReplaceSWModuleValid = () =>
    isReplaceSWModuleDetailsValid() && discp?.trim()?.length >= 3;

  const isUpdateDraftSWComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      !nameError &&
      !versionError &&
      !descriptionError &&
      selectedVfIds?.length > 0 &&
      name?.trim()?.length >= 3 &&
      // regexMin3?.test(name?.trim()) &&
      supplierName?.length > 0 &&
      version?.trim() !== "" &&
      version?.trim()?.length >= 3 &&
      !versionError &&
      regexMin3?.test(version?.trim()) &&
      description?.trim() !== "" &&
      description?.trim()?.length >= 3 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3) &&
      (githubLink?.trim()?.length === 0 || githubLink?.trim()?.length >= 3)
    );
  };
  const isUpdateAgainDraftSWComponentValid = () => {
    return (
      name?.trim() !== "" ||
      supplierName?.length > 0 ||
      version?.trim() !== "" ||
      description?.trim() !== "" ||
      summary?.trim() !== "" ||
      selectedVfIds?.length > 0 ||
      githubLink?.trim() !== ""
    );
  };
  const handleCloseSWModuleReplace = () => {
    setOpenSWModuleReplaceDialog(false);

    clearReplaceSWModuleFields();

    setSelectedSWModuleReplaceDetails(null);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
  };

  const handleReplaceClick = () => {
    setShowConfirmation(true); // Show confirmation dialog when update is clicked
  };

  const handleUpdateDraftClickSW = () => {
    const reqObj = {
      supplier_name: supplierName,
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      version: version?.trim(),
      save_as_draft: false,
      name: name?.trim(),
      ...(summary && { summary }),
      description: description,
      vf_ids: selectedVfIds,
    };
    dispatch(
      HandleApiActions({
        api_name: "/VLC/swm/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_SW_MODULE_DRAFT,
        description: "Add in Save As Draft SWM Module",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() =>
      dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: Number(selectedSWModuleReplaceDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
            zone: selectedZone,
          },
          show_toast: false,
        }),
      ),
    );
    handleCloseSWModuleReplace();
  };
  const handleUpdateAgainDraftClickSW = () => {
    const reqObj = {
      ...(supplierName ? { supplier_name: supplierName } : null),
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      ...(version ? { version: version?.trim() } : null),
      save_as_draft: true,
      name: name?.trim(),
      ...(summary && { summary }),
      ...(description ? { description: description } : null),
      ...(selectedVfIds?.length > 0 ? { vf_ids: selectedVfIds } : null),
      attributes: attributeFormData?.added || [],
    };
    dispatch(
      HandleApiActions({
        api_name: "/VLC/swm/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_SW_MODULE_DRAFT,
        description: "Add in Save As Draft SWM Module",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() =>
      dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: Number(selectedSWModuleReplaceDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
            zone: selectedZone,
          },
          show_toast: false,
        }),
      ),
    );
    handleCloseSWModuleReplace();
  };

  const updateAttributes = async () => {
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];

    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];

    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    if (
      addedAttributes.length === 0 &&
      updatedAttributes.length === 0 &&
      deletedAttributes.length === 0
    ) {
      return;
    }

    const attributeResponse = await dispatch(
      HandleApiActions({
        api_name: "/VLC/attribute/swm/update",
        https_method: "PUT",
        params: {
          entity_id: Number(selectedSWModuleReplaceDetails?.swm_id),
          new_attributes: addedAttributes,
          updated_attributes: updatedAttributes,
          deleted_attributes: deletedAttributes,
        },
        show_toast: false,
      }),
    );

    if (attributeResponse?.error) {
      setDisableActionButton(false);
    }
  };

  const confirmReplaceAndRedirect = async ({
    selectedSWModuleReplaceDetails,
    willImptIntrOnly = false,
  }) => {
    setDisableActionButton(true);
    if (
      selectedSWModuleReplaceDetails &&
      selectedSWModuleReplaceDetails?.swm_id
    ) {
      await updateAttributes();

      const reqObj = {
        ...(supplierName && { supplier_name: supplierName }),
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        variants_ids: selectedEEComponentDetails?.variant_ids,
        version: version?.trim(),
        name: name?.trim(),
        ...(summary && { summary }),
        description: description,
        vf_ids: selectedVfIds,
        attributes: attributeFormData?.added || [],
        will_it_impact: true,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        );
        if (!response.error) {
          handleCloseSWModuleReplace();
          willImptIntrOnly
            ? handleActionType2DialogOpen(selectedSWModuleReplaceDetails)
            : handleActionType1Dialogpen(selectedSWModuleReplaceDetails);
          if (willImptIntrOnly) {
            const resp = await dispatch(
              HandleApiActions({
                ...labCar.update_action_type,
                params: {
                  current_time: new Date().toISOString(),
                  project_id: Number(
                    selectedSWModuleReplaceDetails?.project_id,
                  ),
                  variant_id: Number(selectedSingleVariantDetails?.variant_id),
                  ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
                  swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
                  is_dependency_linking: true,
                },
                show_toast: false,
              }),
            );
          }
          getSWModulesList({ force: true });
        }
      } catch (error) {
        console.error("Failed to replace", error);
      }
    }
    setDisableActionButton(false);
  };

  const handleCloseSWModuleReplaceDialog = () => {
    setOpenSWModuleReplaceDialog(false);
  };

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      tickets_type: 1,
    };

    try {
      // First API call
      const response = await dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets,
          params: reqObj,
          show_toast: false,
        }),
      );
      handleCloseSWModuleReplaceDialog();
      setShowIntersectionImpactPrompt(false);

      // Extract and deduplicate standards
      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              ?.map((ticket) => ticket?.ls_details?.standard_number)
              ?.filter((std) => Boolean(std)) // remove empty/undefined
            : [],
        ),
      ];

      if (standardsWithRuleSet?.length > 0) {
        // Second API call with standards
        const standardWithRuleSet = await dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_engine_with_standards,
            params: { standard: standardsWithRuleSet },
            show_toast: false,
          }),
        );

        if (
          standardWithRuleSet?.data &&
          (Array.isArray(standardWithRuleSet.data)
            ? standardWithRuleSet?.data?.length > 0
            : Object.keys(standardWithRuleSet?.data)?.length > 0)
        ) {
          // Handle the case where there are standards with rulesets
          setShowRuleSetListDialog(true);
          // store the full rule objects so we can send the id later
          setRuleSetList(standardWithRuleSet?.data);
        } else {
          replaceChangesWithoutStandardAndClose();
        }
        return standardWithRuleSet.data || [];
      } else {
        replaceChangesAndClose();
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch standard with ruleset", error);
      return false;
    }
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseSWModuleReplace();
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_rule_set_tree,
          params: {
            rule_set_id: ruleSetIdToUse,
          },
          show_toast: false,
        }),
      );

      if (response?.data) {
        // Store the full tree and open the question dialog
        setRuleSetTree(response.data);
        // Normalize root node into { type, data }
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setRulePath([{ node: root, answer: null }]);
          setShowRuleSetQuestionDialog(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch rule set questions", error);
    }
  };

  const replaceChangesWithoutStandardAndClose = async () => {
    await updateAttributes();
    const reqObj = {
      ...(supplierName && { supplier_name: supplierName }),
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      version: version?.trim(),
      name: name?.trim(),
      ...(summary && { summary }),
      description: description,
      vf_ids: selectedVfIds,
      will_it_impact: false,
      reason: discp,
      requires_homologation: false,
    };

    try {
      const response = await dispatch(
        HandleApiActions({
          ...labCar.replace_sw_module,
          params: reqObj,
          show_toast: true,
        }),
      );



      if (response?.data?.snow_resp) {
        // small delay ensures backdrop cleanup
        setTimeout(() => {
          setTicketsDialogOpen(true);
        }, 200);
      }
      // setDiscp("");
      await dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: Number(selectedSWModuleReplaceDetails?.project_id),
            ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            zone: selectedZone,
          },
          show_toast: false,
        }),
      );
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const replaceChangesAndClose = async () => {
    // Get requires_homologation value from the last node in the rule path
    const lastNode = rulePath[rulePath?.length - 1]?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;
    if (
      selectedSWModuleReplaceDetails &&
      selectedSWModuleReplaceDetails?.swm_id
    ) {
      const reqObj = {
        supplier_name: supplierName,
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        variant_ids: selectedEEComponentDetails?.variant_ids,
        name: name?.trim(),
        ...(summary && { summary }),
        description: description,
        vf_ids: selectedVfIds,
        version: version?.trim(),
        will_it_impact: false,
        requires_homologation: requiresHomologation,
        reason: discp,
        git_link: githubLink,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        );
        if (!response.error) {
          handleCloseSWModuleReplace();
        }
        if (response?.data?.snow_resp) {
          setTimeout(() => {
            setTicketsDialogOpen(true);
          }, 200);
        }
        await dispatch(
          HandleApiActions({
            ...labCar.get_sw_modules,
            params: {
              project_id: Number(selectedSWModuleReplaceDetails?.project_id),
              ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              zone: selectedZone,
            },
            show_toast: false,
          }),
        );
      } catch (error) {
        console.error("Failed to replace", error);
      }
    }
  };

  const handleReplaceSWModule = ({ }) => {
    if (
      selectedSWModuleReplaceDetails &&
      selectedSWModuleReplaceDetails?.swm_id
    ) {
      handleSWModuleReplace({
        supplier_name: supplierName,
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        version: version?.trim(),
        name: name?.trim(),
        summary: summary,
        description: description,
        vf_ids: selectedVfIds?.map(Number) || [],
      });

      handleCloseSWModuleReplace();
    }
  };

  const handleActionType1Dialogpen = (selectedSWModuleReplaceDetails) => {
    setSelectedSWM(selectedSWModuleReplaceDetails);
    setActionType1DialogOpen(true);
  };
  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogOpen = (selectedSWModuleReplaceDetails) => {
    setSelectedSWM(selectedSWModuleReplaceDetails);
    setActionType2DialogOpen(true);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  // Ensure the current variant is selected when opening the dialog
  useEffect(() => {
    if (
      openSWModuleReplaceDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      setSelectedVariantsManySW((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr?.map(String).includes(String(id));
        if (hasId) return prevArr;
        return [id, ...prevArr];
      });
    }
  }, [openSWModuleReplaceDialog, selectedSingleVariantDetails]);
  const replaceComponentAndCheckForDecisionMatrix = async () => {
    try {


      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.re_create_ticket,
          params: {
            execution_id: executionId,
            entity_type: "swm",
            entity_id: Number(selectedSWModuleReplaceDetails?.swm_id),
            reason: discp,
          },
          show_toast: true,
        }),
      );


      // await loadDecisionMatrixContext();
      handleCloseSWModuleReplace();
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };
  const loadDecisionMatrixContext = async () => {
    // 1. Fetch context based on component
    const standardsRes = await dispatch(
      HandleApiActions({
        ...ruleEngineActions.decision_matrix_get_context,
        params: {
          component_type: "SWM",
          component_id: selectedSWModuleReplaceDetails.swm_id,
          project_id: selectedProjectDetails.project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
        },
        show_toast: false,
      }),
    );
    setOpenSWModuleReplaceDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;

    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);

    setShowDecisionMatrix(true);
  };
  const isDecisionMatrixContextValid = (data) => {
    if (!data?.standards || data.standards.length === 0) {
      return false;
    }

    return data.standards.some((standard) => {
      if (!standard.rule_sets || standard.rule_sets.length === 0) {
        return false;
      }

      return standard.rule_sets.some(
        (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
      );
    });
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_start,
          params: {
            workflowId: payload.workflowId,
            version: payload.version,
          },
          show_toast: false,
        }),
      );

      setExecutionId(res.executionId);

      return res;
    }

    if (mode === "COMPLETE") {
      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_node_complete,
          params: {
            executionId: payload.executionId,
            nodeId: payload.nodeId,
            input: payload.input,
          },
          show_toast: false,
        }),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = (executionId) => {
    // navigate / open drawer / open audit view
  };

  //function to handel a get a list of tickets for a legal requirements
  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "swm",
        entity_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        ...(standard_name != null ? { standard_name } : {}),
      };

      const res = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_similar_tickets,
          params: reCreateTicketParams,
          show_toast: true,
        }),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handelGetAiSuggestion = (ticket) => {
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "SW Module",
    });
    setOpenAiPanel(true);
    setSelectedTicket(ticket);
  };

  const handelCloseDecisionMatrix = () => {
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    setRulePath([]);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
  };
  return (
    <Box>
      <CustomDialogBoxV2
        open={openSWModuleReplaceDialog}
        onClose={handleCloseSWModuleReplace}
        title={title}
        onDisabled={
          title === "Replace Software Module"
            ? swmTabValue === 0
              ? !isReplaceSWModuleDetailsValid()
              : !isReplaceSWModuleValid()
            : swmTabValue === 0
              ? !isUpdateAgainDraftSWComponentValid()
              : !isUpdateDraftSWComponentValid()
        }
        successButtonName={
          swmTabValue === 0
            ? "Next"
            : title === "Replace Software Module"
              ? "Replace"
              : "Add"
        }
        rejectButtonName={
          title === "Replace Software Module"
            ? "Cancel"
            : swmTabValue === 1
              ? "Save Draft"
              : null
        }
        rejectDisabled={
          title === "Replace Software Module"
            ? false
            : !isUpdateAgainDraftSWComponentValid()
        }
        rejectButtonType={
          title === "Replace Software Module" ? "cancel" : "other"
        }
        onClickSuccess={() => {
          if (swmTabValue == 0) {
            setSwmTabValue(1);
            return;
          }
          title === "Replace Software Module"
            ? handleReplaceClick()
            : handleUpdateDraftClickSW();
        }}
        // onClickSuccess={handleUpdateClick}
        onClickReject={
          title === "Replace Software Module"
            ? handleCloseSWModuleReplace
            : handleUpdateAgainDraftClickSW
        }
        actionButtonStyle={"three"}
        otherButtonName={
          (title === "Replace Software Module" ||
            title === "Add Draft Software Module") &&
            swmTabValue === 1
            ? "Back"
            : null
        }
        onOtherButtonClick={() => setSwmTabValue(0)}
        disabled={false}
        typeForThree={"cancel"}
        otherButtonPlacement={"left"}

      >
        <CustomTabsV2
          defaultTab={swmTabValue}
          value={swmTabValue}
          onTabChange={(newValue) => setSwmTabValue(newValue)}
          tabs={[
            {
              name: "Details",
              content: (
                <Box sx={{ width: "450px" }}>
                  <Stack spacing={2} mb={2}>
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Number"}
                      required={true}
                      value={name}
                      setValue={(value) =>
                        value?.length <= 40 && setName(value)
                      }

                      maxLength={40}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Version"}
                      required={true}
                      type={"text"}
                      fldType={"textR"}
                      value={version}
                      setValue={(value) =>
                        value?.length <= 40 && setVersion(value)
                      }

                      maxLength={40}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Description"}
                      required={true}
                      value={description}
                      setValue={(value) =>
                        value?.length <= 500 && setDescription(value)
                      }

                      maxLength={500}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Summary"}

                      value={summary}
                      setValue={(value) =>
                        value?.length <= 250 && setSummary(value)
                      }

                      maxLength={250}
                    />

                    <CustomSelectV2
                      size={"small"}
                      label={"Select Supplier Name"}
                      optionsList={supplierOptionsList}
                      selectedValue={supplierName || " "}
                      handleOnChange={(e) => setSupplierName(e.target.value)}
                    />


                    <MultiselectConnectorV2
                      required
                      size={"small"}
                      label={"Select Vehicle Functions"}
                      optionsList={selectedSingleVariantDetails?.vehicle_function_details?.map(
                        (el) => {
                          return {
                            value: el.id,
                            label: `${el.abbreviation} - ${el.name}`,
                          };
                        },
                      )}
                      selectedValues={selectedVfIds || " "}
                      setSelectedValues={setSelectedVfIds}
                      placeholder={false}
                    />

                    {title === "Add Draft Software Module" ? (
                      <MultiselectConnectorV2
                        disabled={true}
                        size={"small"}
                        multiple
                        selectedValues={selectedVariantsManySW || " "}
                        setSelectedValues={setSelectedVariantsManySW}
                        label={"Select Variants"}
                        optionsList={variantOptionList}
                        placeholder={false}
                        immutableValues={
                          selectedSingleVariantDetails?.variant_id != null
                            ? [selectedSingleVariantDetails?.variant_id]
                            : []
                        }
                      />
                    ) : null}


                  </Stack>
                </Box>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={"EDIT"}
                    attributeFromApi={
                      selectedSWModuleReplaceDetails?.attributes || []
                    }
                    getFormData={handleAttributeFormData}
                    reason={discp}
                    setReason={setDiscp}
                    reasonError={discpError}
                    showReason={title == "Replace Software Module"}
                  />
                </Stack>
              ),
            },
          ]}
        />
        <CustomDialogBoxV2
          open={showConfirmation}
          title={"Action"}
          onClose={() => {
            setShowConfirmation(false);
            // setDiscp("");
          }}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="No"
                handleOnClick={() => {
                  setShowConfirmation(false);

                  setTimeout(() => {
                    setShowIntersectionImpactPrompt(true);
                  }, 200);
                }}
              />
              <CustomButtonV2
                size="small"
                type="submit"
                btnName="Yes"
                handleOnClick={() =>
                  confirmReplaceAndRedirect({
                    selectedSWModuleReplaceDetails,
                  })
                }
                disabled={disableActionButton}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                // ...componentStyle.font.text,
                fontSize: "15px",
              }}
            >
              Will it impact the existing dependencies?
            </Typography>
          </Box>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={showIntersectionImpactPrompt}
          onClose={() => {
            setShowIntersectionImpactPrompt(false);
          }}
          title={"Action"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="No"
                // handleOnClick={checkForStandardWithRuleSet}
                handleOnClick={() => {
                  loadDecisionMatrixContext();
                  replaceChangesWithoutStandardAndClose();
                }}
              />
              <CustomButtonV2
                size="small"
                type="submit"
                btnName="Yes"
                handleOnClick={() =>
                  confirmReplaceAndRedirect({
                    selectedSWModuleReplaceDetails,
                    willImptIntrOnly: true,
                  })
                }
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography sx={{ ...componentStyle.font.text }}>
              Will it impact the existing intersection?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
      </CustomDialogBoxV2>

      <Box>
        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?swm_id=${selectedSWM?.swm_id}&ee_id=${selectedEEComponentDetails?.ee_id
              }&selectedZone=${selectedZone?.zone_position_name
              }&tabFromurl=${1}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              Software module dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>



        {/* Action Pending Dialog */}
        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            navigate(
              `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id
              }&zone=${selectedZone?.zone_position_name}&swm_id=${selectedSWM?.swm_id
              }&tab=${1}`,
            );
            setActionType2DialogOpen(false);
          }} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              Software module intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>



        {/* Handle Ticket Page Dialog */}

        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets?tab=${1}`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                // ...componentStyle.font.text,
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>


        {/* Rule set list dialog */}
        <CustomDialogBoxV2
          open={showRuleSetListDialog}
          onClose={handleRuleSetListDialogClose}
          title="Select Rule Set"
          onClickSuccess={async () => {
            // fetch and open the question flow for the selected ruleset
            await viewRuleSetQuestions(selectedRuleSet);
            // keep the list dialog closed
            setShowRuleSetListDialog(false);
          }}
          onDisabled={!selectedRuleSet}
          onClickReject={handleRuleSetListDialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Confirm"}
          rejectButtonName={"Cancel"}
        >
          <Stack spacing={2} sx={{ width: "350px", height: "50px" }}>
            <CustomSelectV2
              size={"small"}
              label={"Select Rule Set"}
              required={true}
              // variant={"standard"}
              optionsList={
                ruleSetList?.map((rs) => ({
                  value: rs?.rule_set_id || rs?.id,
                  label: rs?.name,
                })) || []
              }
              selectedValue={selectedRuleSet || " "}
              handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
            // sltFldStyle={{ width: "100%", marginTop: 6 }}
            />
          </Stack>
        </CustomDialogBoxV2>



        {/* Rule set question dialog - progressive yes/no navigation */}
        <CustomDialogBoxV2
          open={showRuleSetQuestionDialog}
          onClose={() => {
            setShowRuleSetQuestionDialog(false);
            setRuleSetTree(null);
            setRulePath([]);
            setSelectedRuleSet("");
          }}
          title={`Rule Set: ${ruleSetTree?.name || ""}`}
          contentSx={{ minHeight: "350px", width: "450px" }}

        >
          <DialogContent>
            <Stack spacing={2} sx={{}}>
              {/* Full path view - show all questions answered so far and allow editing */}
              <Stack spacing={1}>
                {rulePath?.map((entry, idx) => {
                  const isCategory = entry.node?.type === "category";
                  return (
                    <Box
                      key={idx}
                      sx={{
                        border: "1px solid #e0e0e0",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          idx === rulePath?.length - 1 ? "#fafafa" : "white",
                      }}
                    >
                      {!isCategory ? (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.node?.data?.text}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Box>
                              <CustomButtonV2

                                btnName={"Yes"}
                                handleOnClick={() => handleAnswerAt(idx, "yes")}
                                {...(entry.answer === "yes"
                                  ? { type: "submit" }
                                  : { type: "cancel" })}


                              />
                            </Box>
                            <Box>
                              <CustomButtonV2

                                btnName={"No"}
                                handleOnClick={() => handleAnswerAt(idx, "no")}
                                {...(entry.answer === "no"
                                  ? { type: "submit" }
                                  : { type: "cancel" })}


                              />
                            </Box>
                          </Box>
                        </Stack>
                      ) : (
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session Ended: {entry.node?.data?.description}
                          </Typography>

                        </Stack>
                      )}
                    </Box>
                  );
                })}

                {/* If last node is category show Finish, otherwise allow answering last question if unanswered */}
                {
                  rulePath?.length > 0 &&
                    rulePath[rulePath?.length - 1]?.node?.type === "category" ? (
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="center"
                      mt={2}
                    >
                      <CustomButtonV2
                        type="submit"
                        btnName={"Finish"}
                        // sx={{ ...appTheme.commonBtnStyle }}
                        handleOnClick={() => {
                          // Check if the last node requires homologation
                          const lastNode = rulePath[rulePath?.length - 1]?.node;

                          if (lastNode?.data?.requires_homologation) {
                            // If homologation is required, just call the updateChangesAndClose function and close the dialog
                            replaceChangesAndClose();
                            setShowRuleSetQuestionDialog(false);
                            setRuleSetTree(null);
                            setRulePath([]);
                            setSelectedRuleSet("");
                            handleCloseSWModuleReplace();
                          } else {
                            // If homologation is not required, just close the dialog
                            replaceChangesAndClose();
                            setShowRuleSetQuestionDialog(false);
                            setRuleSetTree(null);
                            setRulePath([]);
                            setSelectedRuleSet("");
                            handleCloseSWModuleReplace();
                          }
                        }}
                      />
                    </Stack>
                  ) : null

                }
              </Stack>
            </Stack>
          </DialogContent>
        </CustomDialogBoxV2>


      </Box>
      <Dialog
        open={showDecisionMatrix}
        onClose={handelCloseDecisionMatrix}
        fullScreen
        PaperProps={{
          sx: {
            width: "100vw",
            height: "100vh",
            margin: 0,
            borderRadius: 0,
          },
        }}
      >
        {/* Deloitte AppBar */}
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: "#26890D",
          }}
        >
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Decision Matrix
            </Typography>
            <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            height: "calc(100vh - 64px)",
            overflow: "auto",
            p: 2,
          }}
        >
          {showDecisionMatrix && (
            <DecisionMatrixPanel
              context={decisionMatrixContext}
              key={showDecisionMatrix ? "open" : "closed"}
              onExecuteWorkflow={handleExecuteWorkflow}
              onViewExecution={handleViewExecution}
              onClose={handelCloseDecisionMatrix}
              onDecisionMatrixAction={replaceComponentAndCheckForDecisionMatrix}
              ////////
              handelGetSimilerTickets={handelGetSimilerTickets}
              similarTickets={similarTickets}
              setSimilarTickets={setSimilarTickets}
              entityDetails={selectedEntity}
              handelGetAiSuggestion={handelGetAiSuggestion}
              selectedTicket={selectedTicket}
              setSelectedTicket={setSelectedTicket}
              openAiPanel={openAiPanel}
              setOpenAiPanel={setOpenAiPanel}
              setCreateTicketData={setCreateTicketData}
              disabledStandards={disabledStandards}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

// Styles
const styles = {
  closeButton: {
    color: "black",
    position: "absolute",
    top: { xs: "2px", sm: "4px" },
    right: { xs: "4px", sm: "8px" },
  },
  swmCompMainContainer: {
    flex: "0 0 auto",
    minWidth: 0,
    width: "100%",
    height: "450px",
    minHeight: "450px",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "stretch",
    boxSizing: "border-box",
    marginBottom: { xs: "8px", sm: "10px", md: "15px" },
  },
  card: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    py: { xs: 0.5, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
    // "& .MuiTypography-root": {
    //   fontSize: { xs: "0.6rem", sm: "0.9rem" },
    // },
  },
  cardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  cardItems: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
    border: "1px solid lightgrey",
  },
  cardItemsTxtContainer: {
    // flexGrow: 1,
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    // display: "flex",
    // flexDirection: "column",
    gap: 0.5,
    // fontSize: { xs: "0.75rem", sm: "0.85rem" },
  },
  noDataAvailableTxt: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  customTextFieldN: {
    width: { xs: "140px", sm: "180px", md: "220px" },
    fontSize: { xs: "0.85rem", sm: "0.95rem" },
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid lightgrey",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  edRpIconsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: 0.5, sm: 1 },
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
};
